/**
 * AppointmentsTab Component
 *
 * REFACTORING STATUS:
 *   Stage 1 (Feb 2026)  — Props interface defined (placeholder).
 *   Stage 2A (Feb 28 2026) — Inventory complete.
 *   Stage 2B (Feb 2026)  — Calendar JSX block lifted out of App.js.
 *     The 2400-line Calendar/Scheduling block now lives here.
 *     Identifiers resolve via either useAppointments() (Section 1) or
 *     useDashboardCalendarBridge() (Section 3 — Dashboard-scope
 *     handlers, data, helpers).  Section 2 (pure utils) imported
 *     directly.
 *   Stage 3 (later) — Pure display helpers will be moved into
 *     `utils/calendar.js` and drag/resize handlers into a
 *     `useCalendarDrag()` hook.  After Stage 3, the bridge prop
 *     disappears and V2 CalendarRoute can render this component
 *     with zero Dashboard coupling.
 */

import React, { Suspense, lazy, useState, useEffect, useRef, useCallback } from 'react';
import { DisabledHeaderDots } from '../shared/HeaderDots';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../components/ui/select';
import {
  Maximize2,
  RefreshCw,
  Undo2,
  Plus,
  Clock,
  Activity,
  Building,
  FileText,
  Edit,
  Info,
  MapPin,
  Phone,
  Video,
  Home,
  Copy,
  GripVertical,
  X,
  Save,
  Calendar as CalendarTitleIcon,
  Search as SearchIcon,
} from 'lucide-react';

import { MultiSelectFilter } from '../shared/MultiSelectFilter';
import { AppointmentHoverCard } from '../shared/AppointmentHoverCard';
import { CalendarDndProvider, DraggableAppt, DroppableCell } from './CalendarDnd';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import FirmEventChips from './FirmEventChips';
import { isSuperAdmin } from '../../utils/helpers';
import { hasOrgWideProviderAccess } from '../../utils/providerAccess';
import { isClientSelfPay, getAppointmentEligibilityIndicator } from '../../utils/eligibilityDisplay';
import { formatLocalDateTime, formatDateTimeLocalInput } from '../../utils/dateFormat';
import {
  formatForDateTimeInput,
  hexToRgb,
  hexToRgba,
  getContrastTextColor,
  getReadableChipTextColor,
  getOverlapInfo,
  getStatusBadgeColor,
  getBillingStatusBorderColor,
  getServiceTypeCategoryColor,
  isTelehealthAppointment,
} from '../../utils/calendar';
import { getBusinessHours, toMinutes } from '../../utils/businessHours';

import { useAppointments } from '../../context/AppointmentsContext';
import { useDashboardCalendarBridge } from '../../context/DashboardCalendarBridgeContext';
import { useCalendarDrag } from '../../hooks/useCalendarDrag';
import { useCurrentTime } from '../../hooks/useCurrentTime';
import { useCalendarPermissions } from '../../hooks/useCalendarPermissions';
import { useAuth } from '../../context/AuthContext';
import { useUserRoleName } from '../../hooks/useUserRoleName';
import { useStaticData } from '../../context/StaticDataContext';
import { useClients } from '../../context/ClientsContext';
import { useNotes } from '../../context/NotesContext';
import { resolveAppointmentCpt } from '../../utils/appointmentDurationRules';
import { DateField } from './DateTimeField';

// Lazy-loaded — the legend popover content is only mounted on click.
const AppointmentColorLegend = lazy(() =>
  import('./AppointmentColorLegend').then((m) => ({
    default: m.AppointmentColorLegend,
  }))
);

const AppointmentsTab = () => {
  // ── Section 1: Appointments context (state + setters) ─────────────
  const {
    appointments, setAppointments,
    selectedAppointment, setSelectedAppointment,
    calendarDate, setCalendarDate,
    calendarView, setCalendarView,
    calendarInterval, setCalendarInterval,
    selectedProviders, setSelectedProviders,
    selectedProvidersMode, setSelectedProvidersMode,
    savedCalendarPrefs, saveCalendarDefaultView, clearCalendarDefaultView,
    showAppointmentForm, setShowAppointmentForm,
    editingAppointment, setEditingAppointment,
    appointmentFormData, setAppointmentFormData,
    resetAppointmentForm,
    appointmentSortAsc, setAppointmentSortAsc,
    showPastAppointments, setShowPastAppointments,
    showCanceledAppointments, setShowCanceledAppointments,
    appointmentSearch, setAppointmentSearch,
    appointmentContextMenu, setAppointmentContextMenu,
    draggedAppointment, setDraggedAppointment,
    resizingAppointment, setResizingAppointment,
    undoStack, setUndoStack,
  } = useAppointments();

  // Managed internal appointment types — resolves internal_type_id →
  // real type name (Chart Review, Training, …) for calendar chip labels.
  const [internalApptTypes, setInternalApptTypes] = useState([]);
  useEffect(() => {
    axios.get(`${API}/internal-appointment-types?include_inactive=true`)
      .then(({ data }) => setInternalApptTypes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);
  const internalTypeName = useCallback((apt) => {
    if (!apt?.internal_type_id) return null;
    return internalApptTypes.find((t) => t.id === apt.internal_type_id)?.name || null;
  }, [internalApptTypes]);
  const internalTypesById = React.useMemo(() => {
    const m = new Map();
    for (const t of internalApptTypes) if (t?.id) m.set(t.id, t);
    return m;
  }, [internalApptTypes]);

  // ── Drag edge-navigation: hovering a dragged appointment over the
  //    left/right screen edge flips to the previous/next week (or day /
  //    month per view) after a short 500ms dwell, keeping the drag alive.
  const edgeFlipTimer = useRef(null);
  const edgeFlipSideRef = useRef(null);
  const edgeFlipFiredRef = useRef(false);
  const edgeFlipDo = useRef(null);
  edgeFlipDo.current = (dir) => {
    const d = new Date(calendarDate);
    if (calendarView === 'week') d.setDate(d.getDate() + 7 * dir);
    else if (calendarView === 'day') d.setDate(d.getDate() + dir);
    else if (calendarView === 'month') d.setMonth(d.getMonth() + dir);
    setCalendarDate(d);
  };
  const cancelEdgeFlip = useCallback(() => {
    edgeFlipSideRef.current = null;
    edgeFlipFiredRef.current = false;
    if (edgeFlipTimer.current) { clearTimeout(edgeFlipTimer.current); edgeFlipTimer.current = null; }
  }, []);
  const armEdgeFlip = (side) => {
    if (edgeFlipSideRef.current !== side && edgeFlipTimer.current) { clearTimeout(edgeFlipTimer.current); edgeFlipTimer.current = null; }
    edgeFlipSideRef.current = side;
    if (edgeFlipTimer.current) return;
    // First flip after 500ms; while still hovering, repeat only every 1.1s
    // so a brief dwell doesn't skip several weeks at once.
    const delay = edgeFlipFiredRef.current ? 1100 : 500;
    edgeFlipTimer.current = setTimeout(() => {
      edgeFlipTimer.current = null;
      edgeFlipFiredRef.current = true;
      edgeFlipDo.current(side === 'left' ? -1 : 1);
    }, delay);
  };
  useEffect(() => { if (!draggedAppointment) cancelEdgeFlip(); }, [draggedAppointment, cancelEdgeFlip]);
  // Safety net: if the drag source unmounts (week flipped away), the browser
  // never fires dragend and the drag state would stay stuck. During a drag,
  // document-level dragover fires continuously — once those heartbeats stop,
  // the drag is over, so clear the state.
  useEffect(() => {
    if (!draggedAppointment) return;
    let hb = null;
    const clear = () => setDraggedAppointment(null);
    const beat = () => {
      if (hb) clearTimeout(hb);
      hb = setTimeout(clear, 700);
    };
    beat();
    const onKey = (e) => { if (e.key === 'Escape') clear(); };
    const onContextMenu = (e) => { e.preventDefault(); clear(); };
    document.addEventListener('dragover', beat, true);
    window.addEventListener('dragend', clear);
    window.addEventListener('keydown', onKey);
    window.addEventListener('contextmenu', onContextMenu, true);
    return () => {
      if (hb) clearTimeout(hb);
      document.removeEventListener('dragover', beat, true);
      window.removeEventListener('dragend', clear);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('contextmenu', onContextMenu, true);
    };
  }, [draggedAppointment, setDraggedAppointment]);
  // Spread onto the Previous/Next nav buttons: hovering a dragged
  // appointment over the button flips the view after the dwell delay.
  const edgeFlipProps = (side) => ({
    onDragOver: (e) => { e.preventDefault(); armEdgeFlip(side); },
    onDragLeave: cancelEdgeFlip,
    onDrop: (e) => { e.preventDefault(); cancelEdgeFlip(); },
  });

  // ── Section 3: Dashboard-scope bridge (Stage 7 slimmed) ──────────
  // Bridge surface shrank again: permission predicates are now
  // owned by useCalendarPermissions, so they leave the bridge.
  const {
    // Quick-Schedule slot click — opens Dashboard's QuickSchedule
    // dialog which still owns `setShowAppointmentForm` etc.
    handleCalendarSlotClick,
    // Action handlers (Dashboard-owned)
    openAppointment, openMeetPopup, openNote,
    refreshAppointments, refreshStats,
    // Display helpers — only the Dashboard-data-bound ones remain.
    getCalendarDays,
    getDOSEligibilityDisplay, getFirmEventsForDate,
    eligibilityTooltip,
    // Dashboard-local UI state for fullscreen toggle
    isFullscreenCalendar, setIsFullscreenCalendar,
    // Aug 2026 — optional V2 list-view window controls (undefined on
    // the legacy Dashboard, which keeps its own fetch strategy).
    onLoadMoreList, listRangeLabel,
  } = useDashboardCalendarBridge();

  // ── Direct context consumption (Stage 6) ─────────────────────────
  const { user } = useAuth();
  const userRoleName = useUserRoleName();
  const { providers, locations, cptCodes, cptBundles, organizations, users: allUsers } = useStaticData();
  const { clients, allClientsForLookup, clientGroups } = useClients();
  const { notes } = useNotes();
  const currentTime = useCurrentTime();

  // ── Permission predicates via hook (Stage 7) ─────────────────────
  const {
    isAdmin,
    canEditAppointment,
    canResizeAppointment,
  } = useCalendarPermissions();

  // ── Stage 4: drag/drop/undo logic owned locally via hook ─────────
  // Dashboard owns its own `useCalendarDrag` instance for the
  // Fullscreen Calendar Dialog; we keep an independent instance
  // here for the inline calendar.  `draggedAppointment` is shared
  // via AppointmentsContext, so a drag started in one surface
  // would still be reconciled if the other surface ever became
  // visible mid-drag (it currently can't — Fullscreen overlays
  // inline — but the contract holds).
  const {
    dragOverSlot, setDragOverSlot,
    addToUndoStack, handleUndo, isUndoing,
    handleDragStart, handleDragOver, handleDrop, handleDragLeave,
    handleAppointmentDragEnd,
  } = useCalendarDrag({ refreshAppointments, cptCodes });

  // Jun 2026 — Ctrl+Z / Cmd+Z wired to the SAME undo stack as the
  // toolbar Undo button (handleUndo).  Ignored while typing in form
  // fields so text-editing undo keeps working.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      if ((e.key || '').toLowerCase() !== 'z') return;
      if (e.repeat) return; // held key auto-repeat must not re-trigger undo
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      e.preventDefault();
      handleUndo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleUndo]);

  // ── List-view full-history client search (Jun 2026) ──────────────
  // The `appointments` state only holds a ~6-week window around the
  // visible month (perf fix), so a client-name search in list view
  // silently missed everything outside that window.  When the search
  // matches client names, fetch those clients' FULL histories from
  // the backend and merge them into the list (dedup by id).
  const [searchExtraAppointments, setSearchExtraAppointments] = useState([]);
  const [searchExtraLoading, setSearchExtraLoading] = useState(false);
  const searchFetchSeq = useRef(0);

  // ── Copay badges on calendar tiles (Jun 2026) ────────────────────
  // Providers asked for a visible cue that a client has a copay.
  // One batched POST per calendar load returns per-client copay +
  // balance + expected-pending info and the dates already collected.
  const [copayFlags, setCopayFlags] = useState({});
  useEffect(() => {
    const ids = [...new Set((appointments || []).map((a) => a.client_id).filter(Boolean))];
    if (ids.length === 0) { setCopayFlags({}); return undefined; }
    const dates = (appointments || []).map((a) => String(a.start_time || '').slice(0, 10)).filter(Boolean).sort();
    let alive = true;
    axios.post(`${API}/copay-flags`, {
      client_ids: ids, start_date: dates[0], end_date: dates[dates.length - 1],
    }).then((r) => { if (alive) setCopayFlags(r.data?.clients || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [appointments]);

  useEffect(() => {
    const term = (appointmentSearch || '').trim().toLowerCase();
    if (calendarView !== 'list' || term.length < 3) {
      setSearchExtraAppointments([]);
      setSearchExtraLoading(false);
      return;
    }
    const matched = (allClientsForLookup || []).filter((c) =>
      c?.id && (
        c.full_name?.toLowerCase().includes(term) ||
        c.preferred_name?.toLowerCase().includes(term)
      )
    ).slice(0, 5);
    if (matched.length === 0) {
      setSearchExtraAppointments([]);
      setSearchExtraLoading(false);
      return;
    }
    const seq = ++searchFetchSeq.current;
    setSearchExtraLoading(true);
    const timer = setTimeout(async () => {
      try {
        const endDate = new Date();
        endDate.setFullYear(endDate.getFullYear() + 2);
        const end = endDate.toISOString().split('T')[0];
        const results = await Promise.all(matched.map((c) =>
          axios.get(`${API}/appointments`, {
            params: { client_id: c.id, summary: true, start_date: '2000-01-01', end_date: end },
          }).then((r) => r.data || []).catch(() => [])
        ));
        if (searchFetchSeq.current === seq) {
          setSearchExtraAppointments(results.flat());
        }
      } finally {
        if (searchFetchSeq.current === seq) setSearchExtraLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [appointmentSearch, calendarView, allClientsForLookup]);

  const renderCopayBadge = (apt) => {
    if (!apt?.client_id) return null;
    const f = copayFlags[apt.client_id];
    if (!f || !(f.copay > 0)) return null;
    const dos = String(apt.start_time || '').slice(0, 10);
    const collected = (f.collected_dates || []).includes(dos);
    const amt = f.copay % 1 === 0 ? f.copay.toFixed(0) : f.copay.toFixed(2);
    const tip = collected
      ? `Copay $${Number(f.copay).toFixed(2)} — collected for this visit`
      : `Copay: $${Number(f.copay).toFixed(2)} per session — not collected for this visit.`
        + (f.client_balance > 0 ? `\nAccount balance: $${Number(f.client_balance).toFixed(2)}` : '')
        + (f.expected_sessions > 0 ? `\n${f.expected_sessions} session(s) awaiting insurance ≈ $${Number(f.expected_amount).toFixed(2)} expected` : '')
        + `\nRemind the client to call the office to pay.`;
    return collected ? (
      <span
        className="text-[9px] ml-1 px-1 py-px rounded bg-green-100 text-green-700 font-semibold whitespace-nowrap align-middle"
        title={tip}
        data-testid={`copay-badge-paid-${apt.id}`}
      >✓ paid</span>
    ) : (
      <span
        className="text-[9px] ml-1 px-1 py-px rounded bg-amber-100 text-amber-800 border border-amber-300 font-semibold whitespace-nowrap align-middle"
        title={tip}
        data-testid={`copay-badge-due-${apt.id}`}
      >${amt} due</span>
    );
  };

  return (
    <CalendarDndProvider
      onDragStartAppt={(apt) => setDraggedAppointment(apt)}
      onDragCancel={() => setDraggedAppointment(null)}
      onDropAppointment={(apt, dropData) => {
        const target = new Date(dropData.date);
        if (dropData.preserveTime && apt.start_time) {
          // Wall-clock transfer (DST-safe): copy local hours/minutes.
          const orig = new Date(apt.start_time);
          target.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
        }
        handleDrop(null, target, { appointmentId: apt.id });
      }}
    >
    <div className="space-y-6">
      {/* Page header — standard position/order across tabs (Jun 2026). */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><CalendarTitleIcon className="h-5 w-5 text-teal-600" />Calendar</h2>
          <p className="text-sm text-gray-500">Schedule and manage appointments</p>
        </div>
          <div className="flex items-center gap-2">
          {(isSuperAdmin(user) || userRoleName === 'provider' || userRoleName === 'office' || userRoleName === 'reception' || user?.is_provider) && (
            <Button 
              data-testid="add-appointment-button" 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => {
                // Reset form and open dialog
                // For office/reception, don't default to their own ID since they're not providers
                const defaultProviderId = (userRoleName === 'office' || userRoleName === 'reception') 
                  ? (selectedProviders?.length === 1 && selectedProviders[0] !== 'all' ? selectedProviders[0] : '')
                  : (user?.id || '');
                setAppointmentFormData({
                  client_id: '',
                  client_ids: [],
                  selected_group_id: '',
                  provider_id: defaultProviderId,
                  start_time: '',
                  end_time: '',
                  service_type: '',
                  appointment_type: 'client_session',
                  status: 'scheduled',
                  is_billable: true,
                  is_payroll_eligible: false,
                  units: 1,
                  location: '',
                  notes: '',
                  cpt_code_id: '',
                  is_recurring: false,
                  recurrence_pattern: '',
                  recurrence_count: '',
                  recurrence_end_date: '',
                  selectedCptMeta: null
                });
                setEditingAppointment(null);
                setShowAppointmentForm(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Schedule Appointment
            </Button>
          )}
          {/* Refresh — top right next to Schedule Appointment, matching
              the improved tab layouts (Jun 2026 user request). */}
          <Button
            variant="outline"
            size="icon"
            onClick={async () => {
              toast.info('Refreshing calendar...');
              try {
                await refreshAppointments(true);
                toast.success('Calendar refreshed');
              } catch (error) {
                console.error('Failed to refresh appointments:', error);
                toast.error('Failed to refresh calendar');
              }
            }}
            title="Refresh calendar data"
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
            data-testid="calendar-refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <DisabledHeaderDots testId="calendar-tools-menu-btn" />
          </div>
      </div>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            {/* Unified provider/calendar picker — uses the shared
                MultiSelectFilter so the trigger, popover, search,
                Include/Exclude, persistent label cache, and
                inline X clear behave identically to the Billing
                tab pickers. Provides a "My Calendar" shortcut
                for self plus "My Assigned" when applicable. */}
            {(() => {
              // Jun 2026 — hasOrgWideProviderAccess covers super admin,
              // plain admin AND office (plain-admin was missing from the
              // old inline check); billers keep their existing access.
              const isAdminOrOffice = (
                hasOrgWideProviderAccess(user, userRoleName)
                || userRoleName === 'office staff'
                || user?.is_biller
                || user?.is_billing_manager
              );
              const supervisees = userRoleName === 'supervisor'
                ? providers.filter(p => p.supervisor_ids && p.supervisor_ids.includes(user?.id))
                : [];

              // Item list visible to this user.  Regular providers
              // see only themselves (collapses to a no-op picker).
              const providerItems = (() => {
                if (isAdminOrOffice) {
                  // All ACTIVE staff — not just is_provider users — so
                  // office staff calendars (personal time, reminders)
                  // are selectable too (Jun 2026 code-gap fix).
                  const staffList = (Array.isArray(allUsers) && allUsers.length ? allUsers : providers) || [];
                  return staffList
                    .filter(p => p && p.id && p.is_active !== false)
                    .map(p => ({
                      id: p.id,
                      label: `${p.full_name || 'Unknown'}${p.credentials ? `, ${p.credentials}` : ''}`,
                      sublabel: p.id === user?.id ? 'me' : (p.is_provider ? undefined : 'staff'),
                    }))
                    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
                }
                if (supervisees.length > 0) {
                  const me = { id: user?.id, label: `${user?.full_name || 'Me'}`, sublabel: 'me' };
                  return [me, ...supervisees.map(p => ({
                    id: p.id,
                    label: `${p.full_name || 'Unknown'}${p.credentials ? `, ${p.credentials}` : ''}`,
                    sublabel: 'supervisee',
                  })).sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))];
                }
                // Plain provider — only me; provide an item so the
                // picker still renders consistently.
                return [{ id: user?.id, label: user?.full_name || 'My Calendar', sublabel: 'me' }];
              })();

              // Normalise the 'all' sentinel some legacy code may
              // still hand us — empty array == "all".
              const selectedArr = Array.isArray(selectedProviders)
                ? selectedProviders.filter(id => id && id !== 'all')
                : [];

              // Shortcut: My Calendar (just me) — matches the
              // pattern Billing uses for "My Assigned".
              const myCalendarIds = user?.id ? [user.id] : [];
              // For supervisors, "My Calendar + Supervisees" is a
              // common quick-toggle; expose as a secondary shortcut
              // via the entity's emptyLabel when supervisees > 0.
              // (Kept the primary shortcut as just "My Calendar"
              // so the chip semantics are unambiguous; supervisors
              // can still tick each supervisee individually.)

              const onChange = (nextIds) => {
                // Empty = "All providers" for admin/office; for
                // plain providers, fall back to [me] to avoid
                // an unfiltered list that the backend would
                // reject.
                if (!nextIds || nextIds.length === 0) {
                  setSelectedProviders(isAdminOrOffice ? ['all'] : (user?.id ? [user.id] : []));
                  return;
                }
                setSelectedProviders(nextIds);
              };

              return (
                <div className="flex items-center gap-1.5">
                  <div className="w-56">
                    <MultiSelectFilter
                      items={providerItems}
                      selected={selectedArr}
                      onChange={onChange}
                      mode={selectedProvidersMode}
                      onModeChange={setSelectedProvidersMode}
                      entity="calendar"
                      emptyLabel={isAdminOrOffice ? 'All Calendars' : 'My Calendar'}
                      shortcuts={[
                        ...(myCalendarIds.length
                          ? [{ label: 'My Calendar', ids: myCalendarIds, testId: 'calendar-provider-filter-shortcut-mine' }]
                          : []),
                        ...(supervisees.length > 0
                          ? [{ label: 'My Supervisees', ids: supervisees.map(p => p.id), testId: 'calendar-provider-filter-shortcut-supervisees' }]
                          : []),
                      ]}
                      triggerTestId="calendar-provider-filter"
                      popoverWidth={300}
                      clearable={isAdminOrOffice}
                    />
                  </div>
                  {/* Mar 2026 — Save / reset the current provider
                      filter as the user's default view.  Loaded
                      automatically on every calendar mount via
                      AppointmentsContext, so the user lands on their
                      chosen providers every time without re-picking.
                      Per-user, private (each user has their own). */}
                  {(() => {
                    const currentIds = (selectedProviders || [])
                      .filter(id => id && id !== 'all')
                      .slice()
                      .sort();
                    const savedIds = ((savedCalendarPrefs?.provider_ids) || [])
                      .slice()
                      .sort();
                    const currentMode = selectedProvidersMode || 'include';
                    const savedMode = savedCalendarPrefs?.mode || 'include';
                    const matchesSaved =
                      savedCalendarPrefs &&
                      currentIds.length === savedIds.length &&
                      currentIds.every((id, i) => id === savedIds[i]) &&
                      currentMode === savedMode;
                    const isEmpty = currentIds.length === 0;
                    return (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              {/* Wrapper span needed so the tooltip
                                  still attaches when the button is
                                  disabled (Radix won't fire on a
                                  disabled trigger directly). */}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-gray-500 hover:text-teal-700 hover:bg-teal-50 disabled:opacity-40 disabled:hover:bg-transparent"
                                disabled={isEmpty || matchesSaved}
                                aria-label="Save current provider filter as default"
                                onClick={async () => {
                                  try {
                                    await saveCalendarDefaultView();
                                    toast.success('Saved as your default calendar view');
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || 'Could not save default view');
                                  }
                                }}
                                data-testid="calendar-save-default-view-btn"
                              >
                                <Save className={`h-4 w-4 ${matchesSaved ? 'text-teal-600' : ''}`} />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs">
                            {isEmpty
                              ? 'Pick at least one provider before saving as default'
                              : matchesSaved
                                ? 'This is already your saved default view'
                                : 'Save as default — applied on every calendar load'}
                          </TooltipContent>
                        </Tooltip>
                        {savedCalendarPrefs && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                aria-label="Clear saved default calendar view"
                                onClick={async () => {
                                  try {
                                    await clearCalendarDefaultView();
                                    toast.success('Default calendar view cleared');
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || 'Could not clear default');
                                  }
                                }}
                                data-testid="calendar-clear-default-view-btn"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs">
                              Clear saved default — calendar will show All Calendars on next load
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
      
          <div className="flex items-center space-x-1 ml-4">
            {/* View picker — dropdown replaces the four buttons
                (Jun 2026 user request). */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">View:</span>
              <Select value={calendarView} onValueChange={setCalendarView}>
                <SelectTrigger className="h-9 w-[110px] text-sm" data-testid="calendar-view-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="list">List</SelectItem>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
        
            {/* Fullscreen Calendar Button */}
            <Button 
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreenCalendar(true)}
              title="Open calendar in fullscreen mode"
              className="ml-2"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>

            {/* Appointment outline-color key — sits with the other
                utility icons so the action cluster (Week/List/Day/Month)
                stays uncluttered. Lazy-loaded — popover content is
                only mounted on click. */}
            <Suspense fallback={null}>
              <AppointmentColorLegend />
            </Suspense>
        
            {/* Undo Button */}
            <div className="ml-2 border-l pl-2 flex items-center gap-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={handleUndo}
                data-testid="calendar-undo-btn"
                disabled={undoStack.length === 0 || isUndoing}
                title={undoStack.length > 0 ? `Undo last change (${undoStack.length} available)` : 'Nothing to undo'}
                className="flex items-center gap-1"
              >
                <Undo2 className="h-4 w-4" />
                Undo {undoStack.length > 0 && `(${undoStack.length})`}
              </Button>
            </div>
        
            {/* Show Canceled Toggle */}
            <div className="ml-2 border-l pl-2 flex items-center space-x-2">
              <input
                type="checkbox"
                id="show-canceled"
                checked={showCanceledAppointments}
                onChange={(e) => setShowCanceledAppointments(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="show-canceled" className="text-sm cursor-pointer">
                Show Canceled
              </Label>
            </div>

            {/* Show Past Toggle (Feb 2026 user request — parity
                with the List view's toggle so past appointments
                on the calendar grid can be hidden if they
                clutter the schedule.  Defaults ON so past
                visits always appear unless the biller chooses
                otherwise.) */}
            <div className="ml-2 border-l pl-2 flex items-center space-x-2">
              <input
                type="checkbox"
                id="show-past-calendar"
                checked={showPastAppointments}
                onChange={(e) => setShowPastAppointments(e.target.checked)}
                className="rounded"
                data-testid="calendar-show-past-toggle"
              />
              <Label htmlFor="show-past-calendar" className="text-sm cursor-pointer">
                Show Past
              </Label>
            </div>
          </div>
      
        </div>
      </CardHeader>
      <CardContent>
        {(() => {
          // Filter appointments based on selected providers
          // Filter appointments by provider and canceled status
          // Mode:
          //   include (default) — show only appointments whose provider_id is in selectedProviders
          //     (legacy 'all' sentinel = show every provider)
          //   exclude — show every provider EXCEPT those in selectedProviders.
          // Invited-user appointments are always visible regardless of mode so an
          // invited clinician can still see meetings hosted on a calendar they hid.
          const _isExclude = selectedProvidersMode === 'exclude';
          const _selected = (selectedProviders || []).filter(id => id && id !== 'all');
          let filteredAppointments;
          if (selectedProviders.includes('all') || (_selected.length === 0 && !_isExclude)) {
            filteredAppointments = (appointments || []);
          } else if (_isExclude) {
            filteredAppointments = (appointments || []).filter(apt =>
              !_selected.includes(apt.provider_id) ||
              (apt.invited_user_ids || []).includes(user?.id)
            );
          } else {
            filteredAppointments = (appointments || []).filter(apt =>
              _selected.includes(apt.provider_id) ||
              (apt.invited_user_ids || []).includes(user?.id)
            );
          }
      
          // Filter out canceled appointments if toggle is off
          if (!showCanceledAppointments) {
            filteredAppointments = filteredAppointments.filter(apt => 
              apt.status !== 'canceled' && 
              apt.status !== 'cancelled' && 
              apt.status !== 'client_cancelled'
            );
          }

          // Feb 2026 — Show Past toggle.  When off, hide
          // appointments whose start_time is BEFORE the start
          // of today.  Default is on so past visits always
          // render unless the biller explicitly hides them.
          if (!showPastAppointments) {
            const _todayStart = new Date();
            _todayStart.setHours(0, 0, 0, 0);
            filteredAppointments = filteredAppointments.filter((apt) => {
              const ts = new Date(apt.start_time);
              return !Number.isNaN(ts.getTime()) && ts >= _todayStart;
            });
          }
      
          // Feb 2026 perf fix — Schedule tab Calendar week-view
          // grid was burning 80%+ of CPU during interactions
          // (Chrome trace).  Root cause: a 24-hour × 7-day
          // calendar grid has 672 cells (at 15-min slots), and
          // each cell was running `filteredAppointments.filter`
          // (O(N) per cell) plus a `.find` for the ongoing-
          // overlap check.  With 2000 appts × 672 cells =
          // ~1.3M ops per render, repeated on every state
          // change.
          //
          // Solution: bucket appointments ONCE into a Map keyed
          // by `${ms}_${hour}_${slot}`, then each cell does an
          // O(1) Map.get instead of O(N) filter.  A second Map
          // groups appointments by day so the ongoing-overlap
          // check only scans that day's appointments.  A third
          // Map gives O(1) client lookup by id.
          //
          // calendarInterval can be 5/15/30 min — bucket by
          // whatever it currently is so the keys match the
          // cell's roundedMinutes computation exactly.
          const slotApptMap = new Map();
          const dayApptMap = new Map();
          // ── Continuation-slot marker (Feb 2026) ────────────────────
          // Provider report: a 12:00-12:20 appt was rendered in the
          // 12:00 slot only, so the day-view's 12:15 slot still
          // appeared "Available" and could be clicked to schedule a
          // conflicting appt.  Track every slot the appt OVERLAPS
          // (start through end-1ms, in calendarInterval steps) so the
          // cell renderer can suppress the Available placeholder /
          // disable click-to-schedule on continuation slots.
          const occupiedSlots = new Set();
          for (const apt of filteredAppointments) {
            const aptStart = new Date(apt.start_time);
            if (Number.isNaN(aptStart.getTime())) continue;
            const dayKey = `${aptStart.getFullYear()}_${aptStart.getMonth()}_${aptStart.getDate()}`;
            const slot = Math.floor(aptStart.getMinutes() / calendarInterval) * calendarInterval;
            const slotKey = `${dayKey}_${aptStart.getHours()}_${slot}`;
            let bucket = slotApptMap.get(slotKey);
            if (!bucket) { bucket = []; slotApptMap.set(slotKey, bucket); }
            bucket.push(apt);
            // Day-level bucket for the ongoing-overlap scan.
            let dayBucket = dayApptMap.get(dayKey);
            if (!dayBucket) { dayBucket = []; dayApptMap.set(dayKey, dayBucket); }
            dayBucket.push(apt);
            // Continuation-slot fan-out — only mark slots STRICTLY
            // AFTER the start (the start slot already renders the
            // appt block; we don't want to mask it).  Cancelled /
            // no-show appts deliberately stay un-marked so the slot
            // remains clickable for rebooking.
            const s = (apt.status || '').toLowerCase();
            if (s === 'cancelled' || s === 'canceled' || s === 'client_cancelled' || s === 'no_show') continue;
            const aptEnd = new Date(apt.end_time);
            if (Number.isNaN(aptEnd.getTime()) || aptEnd <= aptStart) continue;
            // Walk in calendarInterval steps from the slot AFTER
            // start up to (but excluding) the slot containing end.
            const stepMs = calendarInterval * 60 * 1000;
            for (let t = aptStart.getTime() + stepMs; t < aptEnd.getTime(); t += stepMs) {
              const c = new Date(t);
              const cKey = `${c.getFullYear()}_${c.getMonth()}_${c.getDate()}_${c.getHours()}_${Math.floor(c.getMinutes() / calendarInterval) * calendarInterval}`;
              occupiedSlots.add(cKey);
            }
          }
          // Jun 2026 — per-tenant business-hours visible range (defaults
          // preserve the legacy 08:00–23:00 grid when unconfigured).  If
          // an appointment in the visible window falls outside the
          // configured hours, expand the grid just enough to show it —
          // empty outside-hours slots are never rendered.
          const _bh = getBusinessHours(organizations);
          let calStartHour = Math.floor(toMinutes(_bh.start) / 60);
          let calEndHourExcl = Math.max(calStartHour + 1, Math.ceil(toMinutes(_bh.end) / 60));
          {
            let _rangeStart = null;
            let _rangeEnd = null;
            if (calendarView === 'day') {
              _rangeStart = new Date(calendarDate);
              _rangeStart.setHours(0, 0, 0, 0);
              _rangeEnd = new Date(_rangeStart);
              _rangeEnd.setDate(_rangeEnd.getDate() + 1);
            } else if (calendarView === 'week') {
              _rangeStart = new Date(calendarDate);
              _rangeStart.setDate(calendarDate.getDate() - calendarDate.getDay());
              _rangeStart.setHours(0, 0, 0, 0);
              _rangeEnd = new Date(_rangeStart);
              _rangeEnd.setDate(_rangeEnd.getDate() + 7);
            }
            if (_rangeStart) {
              for (const apt of filteredAppointments) {
                const s = new Date(apt.start_time);
                if (Number.isNaN(s.getTime()) || s < _rangeStart || s >= _rangeEnd) continue;
                calStartHour = Math.min(calStartHour, s.getHours());
                calEndHourExcl = Math.max(calEndHourExcl, Math.min(24, s.getHours() + 1));
                const e = new Date(apt.end_time);
                if (!Number.isNaN(e.getTime()) && e > s) {
                  calEndHourExcl = Math.max(
                    calEndHourExcl,
                    Math.min(24, e.getHours() + (e.getMinutes() > 0 ? 1 : 0)),
                  );
                }
              }
            }
          }
          const visibleHours = Array.from(
            { length: calEndHourExcl - calStartHour },
            (_, i) => i + calStartHour,
          );

          // Client-id → client lookup for the per-appointment
          // chip render (was Array#find on a 5000-client array
          // for every appt in every cell, every render).
          const clientByIdMap = new Map();
          for (const c of (allClientsForLookup || [])) if (c?.id) clientByIdMap.set(c.id, c);
          for (const c of (clients || [])) if (c?.id && !clientByIdMap.has(c.id)) clientByIdMap.set(c.id, c);

          // Resolve the calendar chip label for an appointment.  When
          // the client record has a ``preferred_name`` distinct from
          // their legal first name, append it in parens — matches the
          // legacy Dashboard's ``formatClientName`` ("LASTNAME, First
          // (Preferred)" pattern).  Fall back to the denormalized
          // ``apt.client_name`` snapshot if the lookup misses.
          const getApptClientLabel = (lookup, apt) => {
            // Internal appointments (supervision, meetings, etc.) have no
            // client — show the appointment type instead of 'Unknown Client'.
            const isInternal = apt?.internal_type_id ||
              apt?.appointment_type === 'supervision' || apt?.appointment_type === 'internal';
            if (isInternal && !lookup?.full_name && !apt?.client_name) {
              const t = String(internalTypeName(apt) || apt?.internal_type_name || apt?.service_type || '').trim();
              if (t) return t.charAt(0).toUpperCase() + t.slice(1);
              return apt?.appointment_type === 'supervision' ? 'Supervision' : 'Internal Appointment';
            }
            const base = lookup?.full_name || apt?.client_name || 'Unknown Client';
            const preferred = (lookup?.preferred_name || '').trim();
            if (!preferred) return base;
            const first = (lookup?.first_name || '').trim();
            if (first && preferred.toLowerCase() === first.toLowerCase()) return base;
            if (base.toLowerCase().includes(`(${preferred.toLowerCase()})`)) return base;
            return `${base} (${preferred})`;
          };

          // Feb 2026 perf fix — notes-by-appointment lookup
          // for the billing-status border color helper.  Was
          // doing `notes.find(n => n.appointment_id === id)`
          // per appointment per render (200 appts × 2000
          // notes = 400K ops/render).  We only need
          // appointment-linked notes here, so build the Map
          // sparsely (skip notes without an appointment_id).
          const notesByApptMap = new Map();
          for (const n of (notes || [])) {
            if (n?.appointment_id) notesByApptMap.set(n.appointment_id, n);
          }

          // CPT-code-by-id lookup for the service-color helper.
          // Only handles the cpt_code_id case; the
          // service_type substring-match fallback inside the
          // helper still scans the array for legacy data.
          const cptByIdMap = new Map();
          for (const c of (cptCodes || [])) if (c?.id) cptByIdMap.set(c.id, c);

          return calendarView === 'month' ? (
          <div className="space-y-4">
            {/* Drag Indicator Banner */}
            {draggedAppointment && (
              <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3 animate-pulse">
                <span className="text-xl">📅</span>
                <div>
                  <p className="font-semibold">Dragging appointment</p>
                  <p className="text-sm text-blue-100">
                    {allClientsForLookup.find(c => c.id === draggedAppointment.client_id)?.full_name || draggedAppointment.service_type}
                  </p>
                </div>
                <span className="text-sm bg-blue-500 px-2 py-1 rounded">Drop to reschedule</span>
              </div>
            )}
        
            {/* Calendar Header (Month view) */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1))}
                {...edgeFlipProps('left')}
                data-testid="calendar-prev-month-btn"
              >
                ‹ Previous
              </Button>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">
                  {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                {/* Jump-to-date — user request Feb 2026. */}
                <DateField
                  compact
                  value={(() => {
                    const d = calendarDate || new Date();
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  })()}
                  onChange={(v) => {
                    if (!v) return;
                    const [y, m, dd] = v.split('-').map(Number);
                    if (!y || !m || !dd) return;
                    if (y < 1970 || y > 2100) return;
                    setCalendarDate(new Date(y, m - 1, dd));
                  }}
                  testId="calendar-month-jump-to-date"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1))}
                {...edgeFlipProps('right')}
                data-testid="calendar-next-month-btn"
              >
                Next ›
              </Button>
            </div>
        
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Days of week header */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-gray-500 border-b">
                  {day}
                </div>
              ))}
          
              {/* Calendar days */}
              {getCalendarDays().map((date, index) => {
                // O(1) day-bucket lookup (Feb 2026 perf fix — was
                // filtering all appts per cell × 42 cells in month
                // view).
                const _mdKey = `${date.getFullYear()}_${date.getMonth()}_${date.getDate()}`;
                const dayAppointments = dayApptMap.get(_mdKey) || [];
                const isCurrentMonth = date.getMonth() === calendarDate.getMonth();
                const isToday = date.toDateString() === new Date().toDateString();
            
                return (
                  <DroppableCell
                    key={index}
                    dropId={`month-cell-${index}`}
                    data={{ date, preserveTime: true }}
                    className={`min-h-[80px] p-1 border border-gray-200 ${
                      !isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white'
                    } ${isToday ? 'bg-blue-50 border-blue-200' : ''}`}
                    onDragOver={handleDragOver}
                    onDrop={(e) => {
                      // Preserve the dragged appointment's original
                      // time-of-day via wall-clock methods (DST-safe).
                      const target = new Date(date);
                      if (draggedAppointment?.start_time) {
                        const orig = new Date(draggedAppointment.start_time);
                        target.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
                      }
                      handleDrop(e, target);
                    }}
                  >
                    <button
                      type="button"
                      className={`text-sm text-left hover:underline hover:text-blue-700 transition-colors ${isToday ? 'font-bold text-blue-600' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCalendarDate(new Date(date));
                        setCalendarView('day');
                      }}
                      title={`Jump to day view for ${date.toLocaleDateString()}`}
                      data-testid={`month-day-jump-${date.toISOString().slice(0, 10)}`}
                    >
                      {date.getDate()}
                    </button>
                    {/* Firm-wide events (holidays, all-staff meetings) rendered ABOVE appointments */}
                    <FirmEventChips events={getFirmEventsForDate(date)} compact />
                    <div className="space-y-1">
                      {dayAppointments.map(apt => {
                        // O(1) client lookup via clientByIdMap (Feb 2026
                        // perf fix — was Array#find on full client list
                        // per appt × 42 cells in month view).
                        const lookupClient = clientByIdMap.get(apt.client_id);
                        const clientDisplayName = getApptClientLabel(lookupClient, apt);
                    
                        const startTime = new Date(apt.start_time);
                    
                        // Get visual styling (Feb 2026 perf — pass O(1) Maps)
                        const serviceColor = getServiceTypeCategoryColor(apt, cptCodes, cptByIdMap, internalTypesById);
                        const billingBorder = getBillingStatusBorderColor(apt, notes, notesByApptMap);
                        const isTelehealth = isTelehealthAppointment(apt);
                        const isNoShow = apt.status === 'no_show';
                        const isCanceled = apt.status === 'cancelled' || apt.status === 'client_cancelled';
                        const isBlockTime = apt.appointment_type === 'block_time';
                    
                        // Determine text color - use darker shade for block_time (Personal Time)
                        const textColorVal = isTelehealth && !isNoShow && !isCanceled 
                          ? '#4F46E5' 
                          : (isNoShow || isCanceled 
                            ? '#9CA3AF' 
                            : (isBlockTime ? '#2D7A65' : getReadableChipTextColor(serviceColor)));
                    
                        return (
                          <DraggableAppt key={apt.id} apt={apt} label={clientDisplayName} disabled={!(userRoleName === 'provider' || isAdmin())}>
                          <div
                            key={apt.id}
                            className={`group relative text-xs p-1 pl-3 rounded hover:shadow-md transition-all ${isNoShow || isCanceled ? 'opacity-50 line-through' : ''} ${draggedAppointment?.id === apt.id ? 'ring-2 ring-blue-500 ring-offset-2 shadow-lg scale-105 z-50' : ''} ${(userRoleName === 'provider' || isAdmin()) ? 'cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-blue-300' : 'cursor-pointer'}`}
                            style={{
                              backgroundColor: draggedAppointment?.id === apt.id 
                                ? 'rgba(59, 130, 246, 0.3)' 
                                : (isNoShow || isCanceled ? 'rgba(156, 163, 175, 0.2)' : hexToRgba(serviceColor, 0.15)),
                              borderLeft: `3px solid ${isNoShow || isCanceled ? '#9CA3AF' : billingBorder}`,
                              color: textColorVal,
                              transform: draggedAppointment?.id === apt.id ? 'scale(1.02)' : 'scale(1)',
                              transition: 'all 0.15s ease-in-out'
                            }}
                            title={`${apt.client_id 
                              ? clientDisplayName
                              : (apt.appointment_type === 'group' 
                                 ? (clientGroups.find(g => g.id === (apt.selected_group_id || apt.group_id))?.name || 'Group Session')
                                 : apt.appointment_type === 'block_time' ? 'Personal Time' :
                                 apt.appointment_type === 'supervision' ? (internalTypeName(apt) || 'Supervision') :
                                 apt.appointment_type === 'internal' ? (internalTypeName(apt) || 'Internal Meeting') :
                                 apt.appointment_type === 'vacation' ? 'Vacation Day' :
                                 apt.appointment_type === 'sick_day' ? 'Sick Day' :
                                 apt.appointment_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()))
                            } - ${apt.appointment_type === 'group' 
                              ? (apt.cpt_code_id ? (cptCodes.find(c => c.id === apt.cpt_code_id)?.code || apt.service_type) : apt.service_type)
                              : apt.service_type}`}
                            onClick={(e) => {
                              e.stopPropagation();
                          
                              // Check if user can edit this appointment
                              if (!canEditAppointment(apt)) {
                                toast.error('You do not have permission to edit this appointment');
                                return;
                              }
                          
                              void 0 && console.log('Opening appointment for edit:', apt.id);
                              void 0 && console.log('Appointment data from calendar:', {
                                pos_code: apt.pos_code,
                                location: apt.location,
                                telehealth_modality: apt.telehealth_modality
                              });
                              // Find the CPT code metadata (exact id first; duplicate
                              // codes make text matching ambiguous)
                              const _allCptM = [...cptCodes, ...cptBundles];
                              const aptCptCode = _allCptM.find(c => c.id === apt.cpt_code_id || c.id === apt.cpt_bundle_id)
                                || resolveAppointmentCpt(apt, cptCodes)
                                || _allCptM.find(c => c.code === apt.service_type || c.display_name === apt.service_type);
                          
                              setAppointmentFormData({
                                client_id: apt.client_id || '',
                                client_ids: apt.client_ids || [],
                                selected_group_id: apt.selected_group_id || '',
                                provider_id: apt.provider_id,
                                start_time: formatDateTimeLocalInput(apt.start_time),
                                end_time: formatDateTimeLocalInput(apt.end_time),
                                service_type: apt.service_type,
                                appointment_type: apt.appointment_type || 'client_session',
                                status: apt.status || 'scheduled',
                                is_billable: apt.is_billable !== undefined ? apt.is_billable : true,
                                is_payroll_eligible: apt.is_payroll_eligible || false,
                                units: apt.units || 1,
                                location: apt.location || '',
                                pos_code: apt.pos_code || '',
                                telehealth_modality: apt.telehealth_modality || '',
                                notes: apt.notes || '',
                                selectedCptMeta: aptCptCode ? {
                                  allows_multiple_units: aptCptCode.allows_multiple_units,
                                  minutes_per_unit: aptCptCode.minutes_per_unit || aptCptCode.duration_minutes || 60,
                                  code: aptCptCode.code || aptCptCode.display_name,
                                  is_group_session: aptCptCode.is_group_session || false
                                } : null
                              });
                              openAppointment(apt);
                            }}
                            draggable={userRoleName === 'provider' || isAdmin()}
                            onDragStart={(e) => handleDragStart(e, apt)}
                            onDragEnd={handleAppointmentDragEnd}
                          >
                            {/* Drag handle - shows on hover for draggable appointments */}
                            {(userRoleName === 'provider' || isAdmin()) && (
                              <div className="absolute -left-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" title="Drag to reschedule">
                                <GripVertical className="h-3 w-3 text-gray-400" />
                              </div>
                            )}
                            <div className="font-medium truncate">
                              {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div className="flex items-center justify-between truncate">
                              <span className="truncate">
                                {apt.client_id 
                                  ? clientDisplayName
                                  : (apt.appointment_type === 'group' 
                                     ? (clientGroups.find(g => g.id === (apt.selected_group_id || apt.group_id))?.name || 'Group Session')
                                     : apt.appointment_type === 'block_time' ? 'Personal Time' :
                                     apt.appointment_type === 'supervision' ? (internalTypeName(apt) || 'Supervision') :
                                     apt.appointment_type === 'internal' ? (internalTypeName(apt) || 'Internal Meeting') :
                                     apt.appointment_type === 'vacation' ? 'Vacation Day' :
                                     apt.appointment_type === 'sick_day' ? 'Sick Day' :
                                     apt.service_type)
                                }
                              </span>
                              {(() => {
                                const ind = getAppointmentEligibilityIndicator(apt, lookupClient);
                                if (!ind) return null;
                                return (
                                  <span className={`text-[10px] ml-1 font-bold ${ind.color}`} title={ind.title}>
                                    {ind.label}
                                  </span>
                                );
                              })()}
                              {renderCopayBadge(apt)}
                            </div>
                          </div>
                          </DraggableAppt>
                        );
                      })}
                    </div>
                  </DroppableCell>
                );
              })}
            </div>
          </div>
        ) : calendarView === 'day' ? (
          /* Day view with hourly slots */
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCalendarDate(new Date(calendarDate.getTime() - 24 * 60 * 60 * 1000))}
                {...edgeFlipProps('left')}
                data-testid="calendar-prev-day-btn"
              >
                ‹ Previous Day
              </Button>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">
                  {calendarDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h3>
                {/* Jump-to-date (Day view) */}
                <DateField
                  compact
                  value={(() => {
                    const d = calendarDate || new Date();
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  })()}
                  onChange={(v) => {
                    if (!v) return;
                    const [y, m, dd] = v.split('-').map(Number);
                    if (!y || !m || !dd) return;
                    if (y < 1970 || y > 2100) return;
                    setCalendarDate(new Date(y, m - 1, dd));
                  }}
                  testId="calendar-day-jump-to-date"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCalendarDate(new Date(calendarDate.getTime() + 24 * 60 * 60 * 1000))}
                {...edgeFlipProps('right')}
                data-testid="calendar-next-day-btn"
              >
                Next Day ›
              </Button>
            </div>
        
            {/* Firm-wide events (holidays, all-staff meetings) for the selected day */}
            {getFirmEventsForDate(calendarDate).length > 0 && (
              <div className="px-2 py-1.5 bg-gray-50 rounded border">
                <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Firm Events</p>
                <FirmEventChips events={getFirmEventsForDate(calendarDate)} compact={false} />
              </div>
            )}

            {/* Interval selector — moved out of the main toolbar (Feb
                2026) per provider request: the top toolbar was wide
                enough to push off-screen.  Now lives on its own row
                directly above the day grid where it visually belongs
                anyway. */}
            <div className="flex items-center justify-end gap-2 px-1">
              <Label className="text-xs text-gray-600">Slot interval:</Label>
              <Select value={calendarInterval.toString()} onValueChange={(value) => setCalendarInterval(parseInt(value))}>
                <SelectTrigger className="w-24 h-7 text-xs" data-testid="calendar-interval-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 min</SelectItem>
                  <SelectItem value="10">10 min</SelectItem>
                  <SelectItem value="15">15 min</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border rounded-lg overflow-hidden relative">
              {/* Current time indicator - simple red line */}
              {(() => {
                const now = currentTime;
                const isToday = calendarDate.toDateString() === now.toDateString();
            
                if (!isToday) return null;
            
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();
            
                // Only show if within the visible calendar hours
                if (currentHour < calStartHour || currentHour >= calEndHourExcl) return null;
            
                // Calculate position
                const hoursSince8AM = currentHour - calStartHour;
                const minutesInHour = currentMinute;
                const slotsPerHour = 60 / calendarInterval;
                const slotHeight = 50;
            
                const topPosition = (hoursSince8AM * slotsPerHour * slotHeight) + (minutesInHour / 60 * slotHeight * slotsPerHour);
            
                return (
                  <div 
                    className="absolute left-0 right-0 z-10 pointer-events-none border-t-2 border-blue-400 opacity-60"
                    style={{ top: `${topPosition}px` }}
                  />
                );
              })()}
          
              {visibleHours.map(hour => {
                const timeSlots = Array.from({ length: 60 / calendarInterval }, (_, i) => i * calendarInterval);
            
                return timeSlots.map(minute => {
                  const timeKey = `${hour}:${minute.toString().padStart(2, '0')}`;
                  const displayTime = new Date(2000, 0, 1, hour, minute).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });
              
                  // O(1) bucket lookup (Feb 2026 perf fix —
                  // Day view 96 cells × 2000 appts = 192K ops
                  // per render; same root cause as the
                  // week-view fix).
                  const _ddKey = `${calendarDate.getFullYear()}_${calendarDate.getMonth()}_${calendarDate.getDate()}`;
                  const slotAppointments = slotApptMap.get(`${_ddKey}_${hour}_${minute}`) || [];
                  const _slotKeyFull = `${_ddKey}_${hour}_${minute}`;
                  const _isOccupiedByOngoing = occupiedSlots.has(_slotKeyFull);

                  // Feb 2026 — exclude cancelled/no-show from
                  // slot-occupation checks so users CAN double-book
                  // over a cancelled/no-show appointment in Day view.
                  const _isInactiveDayAppt = (a) => {
                    const s = (a?.status || '').toLowerCase();
                    return s === 'cancelled' || s === 'canceled' || s === 'client_cancelled' || s === 'no_show';
                  };
                  const activeDaySlotAppointments = slotAppointments.filter(a => !_isInactiveDayAppt(a));

                  return (
                    <DroppableCell
                      key={timeKey}
                      dropId={`day-slot-${hour}-${minute}`}
                      data={{ date: (() => { const d = new Date(calendarDate); d.setHours(hour, minute, 0, 0); return d; })() }}
                      className="border-b border-gray-100 min-h-[50px] flex"
                      onDragOver={handleDragOver}
                      onDrop={(e) => {
                        const dropDate = new Date(calendarDate);
                        dropDate.setHours(hour, minute, 0, 0);
                        handleDrop(e, dropDate);
                      }}
                    >
                      <div 
                        className="w-24 p-2 bg-gray-50 border-r border-gray-200 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                          void 0 && console.log('⏰ Time label clicked:', displayTime);
                          const clickDate = new Date(calendarDate);
                          handleCalendarSlotClick(clickDate, hour, minute);
                        }}
                      >
                        {displayTime}
                      </div>
                      <div 
                        className={`flex-1 p-2 space-y-1 relative ${_isOccupiedByOngoing ? 'cursor-not-allowed bg-teal-50/40' : 'cursor-pointer hover:bg-gray-50'}`}
                        style={{ overflow: 'visible' }}
                        onClick={() => {
                          // Suppress click-to-schedule on continuation
                          // slots so a 20-min appt starting at 12:00
                          // can't be double-booked by clicking 12:15.
                          if (_isOccupiedByOngoing) return;
                          if (activeDaySlotAppointments.length === 0) {
                            const clickDate = new Date(calendarDate);
                            handleCalendarSlotClick(clickDate, hour, minute);
                          }
                        }}
                      >
                        {slotAppointments.map((apt, _aptIdx) => {
                        // O(1) client lookup via clientByIdMap (Feb
                        // 2026 perf fix — was Array#find on 5000
                        // clients per appt per cell).
                        const lookupClient = clientByIdMap.get(apt.client_id);
                        const clientDisplayName = getApptClientLabel(lookupClient, apt);
                        const startTime = new Date(apt.start_time);
                        // ── Duration-based absolute block (Feb 2026) ─
                        // The appt block is absolutely positioned inside
                        // its start-slot cell with HEIGHT rounded UP to
                        // the next slot boundary so a 20-min appt visibly
                        // occupies both the 12:00 and 12:15 slots (provider
                        // request: "instead of continues, can the appointment
                        // just take up the space?").  Multiple appts in
                        // the SAME slot stack visually (rare case) — the
                        // first one extends its full duration, subsequent
                        // ones overlap with a small vertical offset so
                        // both are clickable.
                        // While the user is drag-to-resizing this block,
                        // swap the persisted end_time for the in-flight
                        // ``resizingAppointment.previewEndTime`` so the
                        // block visibly stretches / shrinks under the
                        // cursor (Feb 2026 — provider request).
                        const isBeingResizedDay = resizingAppointment?.id === apt.id;
                        const aptEnd = isBeingResizedDay
                          ? new Date(resizingAppointment.previewEndTime)
                          : new Date(apt.end_time);
                        const actualMin = (aptEnd.getTime() - startTime.getTime()) / 60000;
                        // Clamp short appts to one slot ONLY when the extra
                        // visual length wouldn't run into a sibling that
                        // starts later in this slot (iter472: 10:00-10:10 +
                        // 10:10-10:20 on a 15-min grid must not overlap).
                        const _clampEndMs = startTime.getTime() + Math.max(calendarInterval, actualMin) * 60000;
                        // Scan the whole DAY, not just this slot — a clamped
                        // 11:05-11:15 block reaches 11:20 and can collide with
                        // an 11:15 appt that lives in the NEXT slot bucket.
                        const _clampCollides = (dayApptMap.get(_ddKey) || []).some(o =>
                          o.id !== apt.id && !_isInactiveDayAppt(o) &&
                          new Date(o.start_time).getTime() >= aptEnd.getTime() &&
                          new Date(o.start_time).getTime() < _clampEndMs);
                        const durationMin = _clampCollides
                          ? Math.max(1, actualMin)
                          : Math.max(calendarInterval, actualMin);
                        const slotHeightPx = 50;
                        // Exact proportional geometry (Jun 2026 regression
                        // fix): back-to-back appts (10:00-10:20 → 10:20-10:40)
                        // must abut, not overlap. Start offset = minutes into
                        // the slot; height = true duration (no rounding up to
                        // slot boundaries — continuation-slot clickability is
                        // handled separately via occupiedSlots).
                        const dayMinuteOffsetPx =
                          ((startTime.getMinutes() % calendarInterval) / calendarInterval) * slotHeightPx;
                        // Subtract 8px so block fits the cell's padding
                        // (top:4 + bottom:4) and visually breathes.
                        const blockHeightPx = (durationMin / calendarInterval) * slotHeightPx - 8;
                        // Stack offset only among appts that truly overlap in
                        // TIME — back-to-back appts sharing a start slot
                        // shouldn't get nudged (iter472 fix).
                        const _overlapPeers = slotAppointments.filter(o =>
                          !_isInactiveDayAppt(o) &&
                          new Date(o.start_time).getTime() < aptEnd.getTime() &&
                          startTime.getTime() < new Date(o.end_time).getTime());
                        const stackOffsetPx = Math.max(0, _overlapPeers.findIndex(o => o.id === apt.id)) * 6;
                        const useAbsolute = !Number.isNaN(durationMin) && durationMin > 0;
                        const dayServiceColor = getServiceTypeCategoryColor(apt, cptCodes, cptByIdMap, internalTypesById);
                        const dayIsCanceled = apt.status === 'canceled' || apt.status === 'cancelled' || apt.status === 'client_cancelled';
                        const dayChipNormal = apt.status !== 'no_show' && !dayIsCanceled;
                        return (
                          <DraggableAppt key={apt.id} apt={apt} label={clientDisplayName}>
                          <div
                            key={apt.id}
                            className={`group ${useAbsolute ? 'absolute' : 'relative'} p-2 pl-5 rounded border-l-4 overflow-hidden ${
                              apt.status === 'no_show' 
                                ? 'bg-gray-200 text-gray-500 opacity-60 border-gray-400' 
                                : dayIsCanceled
                                ? 'bg-gray-100 text-gray-400 opacity-50 border-gray-300'
                                : 'hover:shadow-md'
                            } ${(userRoleName === 'provider' || isAdmin()) ? 'cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-blue-300' : 'cursor-pointer'} ${draggedAppointment?.id === apt.id ? 'ring-2 ring-blue-500 shadow-lg' : ''}`}
                            style={{
                              ...(useAbsolute ? {
                              top: `${4 + stackOffsetPx + dayMinuteOffsetPx}px`,
                              left: '8px',
                              right: '8px',
                              height: `${blockHeightPx - stackOffsetPx}px`,
                              zIndex: 5 + _aptIdx,
                              } : {}),
                              ...(dayChipNormal ? {
                                backgroundColor: hexToRgba(dayServiceColor, 0.15),
                                color: getReadableChipTextColor(dayServiceColor),
                                borderLeftColor: dayServiceColor,
                              } : {}),
                            }}
                            draggable={userRoleName === 'provider' || isAdmin()}
                            onDragStart={(e) => handleDragStart(e, apt)}
                            onDragEnd={handleAppointmentDragEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                          
                              // Check if user can edit this appointment
                              if (!canEditAppointment(apt)) {
                                toast.error('You do not have permission to edit this appointment');
                                return;
                              }
                          
                              // Find the CPT code metadata (exact id first; duplicate
                              // codes make text matching ambiguous)
                              const _allCpt = [...cptCodes, ...cptBundles];
                              const aptCptCode = _allCpt.find(c => c.id === apt.cpt_code_id || c.id === apt.cpt_bundle_id)
                                || resolveAppointmentCpt(apt, cptCodes)
                                || _allCpt.find(c => c.code === apt.service_type || c.display_name === apt.service_type);
                          
                              setAppointmentFormData({
                                client_id: apt.client_id || '',
                                client_ids: apt.client_ids || [],
                                selected_group_id: apt.selected_group_id || '',
                                provider_id: apt.provider_id,
                                start_time: formatDateTimeLocalInput(apt.start_time),
                                end_time: formatDateTimeLocalInput(apt.end_time),
                                service_type: apt.service_type,
                                appointment_type: apt.appointment_type || 'client_session',
                                status: apt.status || 'scheduled',
                                is_billable: apt.is_billable !== undefined ? apt.is_billable : true,
                                is_payroll_eligible: apt.is_payroll_eligible || false,
                                units: apt.units || 1,
                                location: apt.location || '',
                                pos_code: apt.pos_code || '',
                                telehealth_modality: apt.telehealth_modality || '',
                                notes: apt.notes || '',
                                selectedCptMeta: aptCptCode ? {
                                  allows_multiple_units: aptCptCode.allows_multiple_units,
                                  minutes_per_unit: aptCptCode.minutes_per_unit || aptCptCode.duration_minutes || 60,
                                  code: aptCptCode.code || aptCptCode.display_name,
                                  is_group_session: aptCptCode.is_group_session || false
                                } : null
                              });
                              openAppointment(apt);
                            }}
                            title="Drag to reschedule"
                          >
                            {/* Drag handle - shows on hover for draggable appointments */}
                            {(userRoleName === 'provider' || isAdmin()) && (
                              <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <GripVertical className="h-4 w-4 text-gray-400" />
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <div className={apt.status === 'no_show' ? 'line-through' : ''}>
                                <div className="flex items-center">
                                  <span className={`font-medium ${apt.google_meet_link ? 'text-[#10B981]' : ''}`}>
                                    {apt.client_id 
                                      ? clientDisplayName
                                      : (apt.appointment_type === 'block_time' ? 'Personal Time' :
                                         apt.appointment_type === 'supervision' ? (internalTypeName(apt) || 'Supervision') :
                                         apt.appointment_type === 'internal' ? (internalTypeName(apt) || 'Internal Meeting') :
                                         apt.appointment_type === 'vacation' ? 'Vacation Day' :
                                         apt.appointment_type === 'sick_day' ? 'Sick Day' :
                                         apt.appointment_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()))
                                    }
                                  </span>
                                  {(() => {
                                    const ind = getAppointmentEligibilityIndicator(apt, lookupClient);
                                    if (!ind) return null;
                                    return <span className={`ml-2 text-xs font-bold ${ind.color}`} title={ind.title}>{ind.label}</span>;
                                  })()}
                                  {renderCopayBadge(apt)}
                                </div>
                                <div className={`text-sm ${apt.google_meet_link ? 'text-[#10B981]' : ''}`}>{apt.service_type}</div>
                                <div className={`text-xs ${isBeingResizedDay ? 'text-blue-600 font-bold' : (apt.google_meet_link ? 'text-[#10B981]' : '')}`}>
                                  {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                                  {aptEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  {isBeingResizedDay && resizingAppointment?.previewDuration && (
                                    <span className="ml-2 text-blue-600">↕ {resizingAppointment.previewDuration}min</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Badge className={getStatusBadgeColor(apt.status)}>
                                  {apt.status}
                                </Badge>
                                {/* Location/POS indicators */}
                                {apt.pos_code === '02' || apt.pos_code === '10' ? (
                                  // Telehealth - show video icon with meet link or just icon
                                  apt.google_meet_link ? (
                                    <Button size="sm" variant="outline" className="h-6 w-6 p-0" asChild onClick={(e) => e.stopPropagation()}>
                                      <a href={apt.google_meet_link} target="_blank" rel="noopener noreferrer" title="Join Telehealth Session">
                                        <Video className="h-3 w-3" />
                                      </a>
                                    </Button>
                                  ) : (
                                    <div className="h-6 w-6 flex items-center justify-center" title="Telehealth Session">
                                      <Video className="h-3 w-3 text-blue-600" />
                                    </div>
                                  )
                                ) : apt.pos_code === '12' ? (
                                  // Home visit
                                  <div className="h-6 w-6 flex items-center justify-center" title={`Home Visit${apt.location ? ` - ${apt.location}` : ''}`}>
                                    <span className="text-base">🏠</span>
                                  </div>
                                ) : apt.pos_code === '11' ? (
                                  // Office visit
                                  <div className="h-6 w-6 flex items-center justify-center" title={`Office Visit${apt.location ? ` - ${apt.location}` : ''}`}>
                                    <span className="text-base">🏢</span>
                                  </div>
                                ) : apt.pos_code === '03' ? (
                                  // School
                                  <div className="h-6 w-6 flex items-center justify-center" title={`School Visit${apt.location ? ` - ${apt.location}` : ''}`}>
                                    <span className="text-base">🏫</span>
                                  </div>
                                ) : apt.location ? (
                                  // Other locations - show generic location icon
                                  <div className="h-6 w-6 flex items-center justify-center" title={apt.location}>
                                    <MapPin className="h-3 w-3 text-gray-600" />
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            {/* ── Day-view bottom resize handle (Feb 2026) ──
                                Provider request: grab the bottom edge of an
                                appt block to extend / shrink without
                                re-opening the dialog.  Mirrors the existing
                                Week-view handle (line ~2064) — same CPT
                                min/max snapping, midnight-guard, units
                                recomputation, optimistic update, undo
                                stack push, and PATCH /api/appointments/{id}.
                                Only mounts when ``canResizeAppointment``
                                (CPT allows variable duration) AND the user
                                has permission (provider self or admin). */}
                            {useAbsolute && canResizeAppointment(apt) && (userRoleName === 'provider' || isAdmin()) && (
                              <div
                                className="resize-handle absolute bottom-0 left-0 right-0 h-1.5 hover:h-2.5 hover:bg-blue-500 cursor-ns-resize transition-all"
                                style={{ backgroundColor: 'rgba(20, 184, 166, 0.6)' }}
                                title="Drag down to extend appointment duration"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const startY = e.clientY;
                                  const startDurationMin = durationMin;
                                  const slotHeightLocal = 50;
                                  const aptStart = new Date(apt.start_time);
                                  const aptCptCode = resolveAppointmentCpt(apt, cptCodes);
                                  const handleMouseMove = (moveEvt) => {
                                    const deltaY = moveEvt.clientY - startY;
                                    const deltaSlots = Math.round(deltaY / slotHeightLocal);
                                    let previewDuration = Math.max(
                                      calendarInterval,
                                      Math.round((startDurationMin + deltaSlots * calendarInterval) / calendarInterval) * calendarInterval,
                                    );
                                    if (aptCptCode) {
                                      if (aptCptCode.allows_multiple_units === false) {
                                        const single = aptCptCode.max_duration || aptCptCode.minutes_per_unit || aptCptCode.duration_minutes || 60;
                                        previewDuration = Math.min(previewDuration, single);
                                      }
                                      if (aptCptCode.max_duration && previewDuration > aptCptCode.max_duration) previewDuration = aptCptCode.max_duration;
                                      if (aptCptCode.min_duration && previewDuration < aptCptCode.min_duration) previewDuration = aptCptCode.min_duration;
                                    }
                                    const newEnd = new Date(aptStart.getTime() + previewDuration * 60000);
                                    setResizingAppointment({ id: apt.id, previewEndTime: newEnd.toISOString(), previewDuration });
                                  };
                                  const handleMouseUp = async (upEvt) => {
                                    const deltaY = upEvt.clientY - startY;
                                    const deltaSlots = Math.round(deltaY / slotHeightLocal);
                                    let newDuration = Math.max(
                                      calendarInterval,
                                      Math.round((startDurationMin + deltaSlots * calendarInterval) / calendarInterval) * calendarInterval,
                                    );
                                    if (aptCptCode?.min_duration && newDuration < aptCptCode.min_duration) {
                                      newDuration = aptCptCode.min_duration;
                                      toast.warning(`Minimum duration for ${aptCptCode.code} is ${newDuration} minutes — snapped to the minimum.`);
                                    } else if (aptCptCode?.max_duration && newDuration > aptCptCode.max_duration) {
                                      newDuration = aptCptCode.max_duration;
                                      toast.warning(`Maximum duration for ${aptCptCode.code} is ${newDuration} minutes — snapped to the maximum.`);
                                    }
                                    const newEnd = new Date(aptStart.getTime() + newDuration * 60000);
                                    if (apt.appointment_type === 'client_session' && aptStart.toDateString() !== newEnd.toDateString()) {
                                      toast.error('Client appointments cannot cross midnight');
                                      document.removeEventListener('mousemove', handleMouseMove);
                                      document.removeEventListener('mouseup', handleMouseUp);
                                      setResizingAppointment(null);
                                      return;
                                    }
                                    const minutesPerUnit = aptCptCode?.minutes_per_unit || aptCptCode?.duration_minutes || 60;
                                    let newUnits = Math.max(1, Math.round(newDuration / minutesPerUnit));
                                    let adjustedEnd = newEnd;
                                    if (aptCptCode?.allows_multiple_units === false && newUnits > 1) {
                                      newUnits = 1;
                                      newDuration = minutesPerUnit;
                                      adjustedEnd = new Date(aptStart.getTime() + newDuration * 60000);
                                      toast.warning(`${aptCptCode.code} does not allow multiple units. Limited to 1 unit (${newDuration} min).`);
                                    }
                                    try {
                                      addToUndoStack('update', {
                                        id: apt.id,
                                        start_time: apt.start_time,
                                        end_time: apt.end_time,
                                        client_id: apt.client_id,
                                        provider_id: apt.provider_id,
                                        service_type: apt.service_type,
                                        units: apt.units,
                                      });
                                      const newEndIso = adjustedEnd.toISOString();
                                      setAppointments((prev) => prev.map((a) =>
                                        a.id === apt.id ? { ...a, end_time: newEndIso, units: newUnits } : a,
                                      ));
                                      await axios.patch(`${API}/appointments/${apt.id}`, {
                                        end_time: newEndIso,
                                        units: newUnits,
                                      });
                                      toast.success(`Appointment updated: ${newDuration}min (${newUnits} units) — Ctrl+Z to undo`);
                                    } catch (err) {
                                      console.error('Resize PATCH failed:', err);
                                      toast.error('Failed to update appointment');
                                      refreshAppointments?.(true);
                                    } finally {
                                      document.removeEventListener('mousemove', handleMouseMove);
                                      document.removeEventListener('mouseup', handleMouseUp);
                                      setResizingAppointment(null);
                                    }
                                  };
                                  document.addEventListener('mousemove', handleMouseMove);
                                  document.addEventListener('mouseup', handleMouseUp);
                                }}
                              />
                            )}
                          </div>
                          </DraggableAppt>
                        );
                      })}
                    
                        {slotAppointments.length === 0 && !_isOccupiedByOngoing && minute === 0 && (
                          <div className="text-gray-400 text-xs italic">Available</div>
                        )}
                        {/* "↑ continues" placeholder is hidden when the
                            absolute appt block covers the continuation
                            slot (Feb 2026 — provider request: "instead
                            of continues, can the appointment just take
                            up the space?").  Kept as fallback for any
                            edge case where the block didn't render. */}
                        {slotAppointments.length === 0 && _isOccupiedByOngoing && false && (
                          <div className="text-teal-700/70 text-[10px] italic select-none" title="Continues from earlier appointment">
                            ↑ continues
                          </div>
                        )}
                      </div>
                    </DroppableCell>
                  );
                });
              }).flat()}
            </div>
          </div>
        ) : calendarView === 'week' ? (
          /* Week Grid View - Times on left, days across top */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  const newDate = new Date(calendarDate);
                  newDate.setDate(newDate.getDate() - 7);
                  setCalendarDate(newDate);
                }}
                {...edgeFlipProps('left')}
                data-testid="calendar-prev-week-btn"
              >
                Previous Week
              </Button>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCalendarDate(new Date())}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  Today
                </Button>
                <h3 className="text-lg font-semibold">
                  Week of {(() => {
                    const ws = new Date(calendarDate);
                    ws.setDate(ws.getDate() - ws.getDay());
                    return ws.toLocaleDateString();
                  })()}
                </h3>
                {/* Jump-to-date (Week view) */}
                <DateField
                  compact
                  value={(() => {
                    const d = calendarDate || new Date();
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  })()}
                  onChange={(v) => {
                    if (!v) return;
                    const [y, m, dd] = v.split('-').map(Number);
                    if (!y || !m || !dd) return;
                    if (y < 1970 || y > 2100) return;
                    setCalendarDate(new Date(y, m - 1, dd));
                  }}
                  testId="calendar-week-jump-to-date"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  const newDate = new Date(calendarDate);
                  newDate.setDate(newDate.getDate() + 7);
                  setCalendarDate(newDate);
                }}
                {...edgeFlipProps('right')}
                data-testid="calendar-next-week-btn"
              >
                Next Week
              </Button>
            </div>

            {/* Week Grid */}
            <div className="border rounded-lg overflow-hidden">
              {/* Header with days */}
              <div className="grid grid-cols-8 border-b bg-gray-50">
                <div className="p-2 text-sm font-medium border-r">Time</div>
                {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                  const dayDate = new Date(calendarDate);
                  dayDate.setDate(calendarDate.getDate() - calendarDate.getDay() + dayOffset);
                  const isToday = dayDate.toDateString() === new Date().toDateString();
                  const firmEvents = getFirmEventsForDate(dayDate);
                  return (
                    <button
                      type="button"
                      key={dayOffset}
                      className={`p-2 text-sm font-medium text-center border-r last:border-r-0 cursor-pointer hover:bg-blue-50 transition-colors ${isToday ? 'bg-blue-100 text-blue-800' : ''}`}
                      onClick={() => {
                        setCalendarDate(new Date(dayDate));
                        setCalendarView('day');
                      }}
                      title={`Jump to day view for ${dayDate.toLocaleDateString()}`}
                      data-testid={`week-day-jump-${dayDate.toISOString().slice(0, 10)}`}
                    >
                      <div className={isToday ? 'font-bold' : ''}>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOffset]}</div>
                      <div className={`text-xs ${isToday ? 'text-blue-700 font-semibold' : 'text-gray-500'}`}>{dayDate.getMonth() + 1}/{dayDate.getDate()}</div>
                      {firmEvents.length > 0 && (
                        <div className="mt-1">
                          <FirmEventChips events={firmEvents} compact />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Time slots - 15 minute increments */}
              <div className="max-h-[600px] overflow-y-auto relative">
                {/* Current time indicator for week view */}
                {(() => {
                  const now = new Date(); // Use fresh Date() for accurate current time
                  const todayDayOfWeek = now.getDay(); // 0-6
              
                  // Check if current day is in the visible week
                  const weekStart = new Date(calendarDate);
                  weekStart.setDate(calendarDate.getDate() - calendarDate.getDay());
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekStart.getDate() + 6);
              
                  const isTodayInWeek = now >= weekStart && now <= weekEnd;
                  if (!isTodayInWeek) return null;
              
                  const currentHour = now.getHours();
                  const currentMinute = now.getMinutes();
              
                  // Only show if within the visible calendar hours
                  if (currentHour < calStartHour || currentHour >= calEndHourExcl) return null;
              
                  // Calculate vertical position
                  const hoursSince8AM = currentHour - calStartHour;
                  const minutesInHour = currentMinute;
                  const slotHeight = 20; // height per 15-min slot
                  const slotsPerHour = 4; // 15-min increments
              
                  const topPosition = (hoursSince8AM * slotsPerHour * slotHeight) + (minutesInHour / 60 * slotHeight * slotsPerHour);
              
                  // Calculate horizontal position (which column)
                  const timeColumnWidth = 100 / 8; // 8 columns total
                  const leftPosition = timeColumnWidth * (todayDayOfWeek + 1); // +1 for time column
                  const columnWidth = timeColumnWidth;
              
                  return (
                    <div 
                      className="absolute z-10 pointer-events-none border-t-2 border-blue-400 opacity-60"
                      style={{ 
                        top: `${topPosition}px`,
                        left: `${leftPosition}%`,
                        width: `${columnWidth}%`
                      }}
                    />
                  );
                })()}
            
                {visibleHours.flatMap(hour => 
                  Array.from({ length: 60 / calendarInterval }, (_, i) => i * calendarInterval).map((minute, idx) => {
                    const timeKey = `${hour}-${minute}`;
                    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const isHourMark = minute === 0;
                
                    return (
                      <div key={timeKey} className={`grid grid-cols-8 ${isHourMark ? 'border-t border-gray-300' : ''}`} style={{height: '20px'}}>
                        {/* Time column - show hour only on :00, invisible for quarters - CLICKABLE */}
                        <div 
                          className={`p-0.5 text-xs border-r bg-gray-50 font-medium relative flex items-start ${isHourMark ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                          style={{height: '20px'}}
                          onClick={() => {
                            if (isHourMark) {
                              void 0 && console.log('⏰ Week view time label clicked:', `${displayHour}:00 ${ampm}`);
                              // Use current week's Monday as base date
                              const clickDate = new Date(calendarDate);
                              clickDate.setDate(calendarDate.getDate() - calendarDate.getDay() + 1); // Monday
                              clickDate.setHours(hour, minute, 0, 0);
                          
                              const endDate = new Date(clickDate.getTime() + 15 * 60 * 1000);
                          
                              // Default provider: use selected provider if one is selected, otherwise empty
                              const defaultProviderId = selectedProviders?.length === 1 ? selectedProviders[0] : '';
                          
                              setAppointmentFormData({
                                client_id: '',
                                provider_id: defaultProviderId,
                                start_time: formatForDateTimeInput(clickDate),
                                end_time: formatForDateTimeInput(endDate),
                                service_type: '',
                                appointment_type: 'client_session',
                                is_billable: true,
                                is_payroll_eligible: false,
                                notes: '',
                                location: '',
                                units: 1
                              });
                              setEditingAppointment(null);
                              setShowAppointmentForm(true);
                            }
                          }}
                        >
                          {isHourMark ? (
                            <span className="text-[11px]">{displayHour}:00 {ampm}</span>
                          ) : (
                            <span className="invisible">:</span>
                          )}
                        </div>

                        {/* Day columns */}
                        {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                          const dayDate = new Date(calendarDate);
                          dayDate.setDate(calendarDate.getDate() - calendarDate.getDay() + dayOffset);
                          dayDate.setHours(hour, minute, 0, 0);

                          // Format for datetime-local input: YYYY-MM-DDTHH:MM
                          const year = dayDate.getFullYear();
                          const month = (dayDate.getMonth() + 1).toString().padStart(2, '0');
                          const day = dayDate.getDate().toString().padStart(2, '0');
                          const hours = hour.toString().padStart(2, '0');
                          const mins = minute.toString().padStart(2, '0');
                          const datetimeLocal = `${year}-${month}-${day}T${hours}:${mins}`;
                      
                          // Calculate end time (default 60 minutes later, will be updated when CPT is selected)
                          const endDate = new Date(dayDate);
                          endDate.setMinutes(endDate.getMinutes() + 15); // Default to 15 min
                          const endYear = endDate.getFullYear();
                          const endMonth = (endDate.getMonth() + 1).toString().padStart(2, '0');
                          const endDay = endDate.getDate().toString().padStart(2, '0');
                          const endHours = endDate.getHours().toString().padStart(2, '0');
                          const endMins = endDate.getMinutes().toString().padStart(2, '0');
                          const endDatetimeLocal = `${endYear}-${endMonth}-${endDay}T${endHours}:${endMins}`;

                          // O(1) bucket lookup (Feb 2026 perf fix —
                          // was a per-cell Array#filter over the
                          // full appointments list, 672 cells ×
                          // 2000 appts = 1.3M ops/render).
                          const _dayKey = `${dayDate.getFullYear()}_${dayDate.getMonth()}_${dayDate.getDate()}`;
                          const slotAppointments = slotApptMap.get(`${_dayKey}_${hour}_${minute}`) || [];
                      
                          // Check if this slot is covered by an ongoing appointment (not starting here)
                          // Scoped to the day's appointments only — was scanning ALL
                          // filteredAppointments per cell with Array#find.
                          const _dayAppts = dayApptMap.get(_dayKey) || [];
                          const ongoingAppointment = _dayAppts.find(apt => {
                            const aptStart = new Date(apt.start_time);
                            const aptEnd = new Date(apt.end_time);
                            return dayDate > aptStart && dayDate < aptEnd;
                          });

                          // Feb 2026 — exclude cancelled / no-show
                          // appointments from the slot-occupation
                          // checks below.  They render at half-
                          // width so the slot CAN take a new
                          // booking, but the click handler was
                          // still treating any slotAppointments
                          // hit as "occupied" and refusing to
                          // open the new-appointment form.  An
                          // active overlap is the only valid
                          // block.
                          const _isInactiveAppt = (a) => {
                            const s = (a?.status || '').toLowerCase();
                            return s === 'cancelled' || s === 'client_cancelled' || s === 'no_show';
                          };
                          const activeSlotAppointments = slotAppointments.filter(a => !_isInactiveAppt(a));
                          const activeOngoing = ongoingAppointment && !_isInactiveAppt(ongoingAppointment) ? ongoingAppointment : null;

                          const slotKey = `${dayDate.getTime()}_${dayOffset}`;
                          const isHoveringOver = dragOverSlot === slotKey && draggedAppointment;
                          const isTodayColumn = dayDate.toDateString() === new Date().toDateString();
                      
                          return (
                            <DroppableCell
                              key={dayOffset}
                              dropId={`week-slot-${slotKey}`}
                              data={{ date: dayDate }}
                              disabled={!!ongoingAppointment}
                              className={`border-r last:border-r-0 overflow-visible relative transition-colors ${
                                isHoveringOver 
                                  ? 'bg-blue-200 ring-2 ring-blue-400 ring-inset' 
                                  : !activeOngoing && activeSlotAppointments.length === 0 
                                  ? `hover:bg-blue-50 cursor-pointer ${isTodayColumn ? 'bg-blue-50/30' : ''}` 
                                  : isTodayColumn ? 'bg-blue-50/30' : ''
                              }`}
                              style={{height: '20px'}}
                              onClick={() => {
                                void 0 && console.log('🗓️ Week view slot clicked:', {hour, minute, dayOffset, isEmpty: !activeOngoing && activeSlotAppointments.length === 0});
                                if (!activeOngoing && activeSlotAppointments.length === 0) {
                                  void 0 && console.log('✅ Opening appointment form from week view');
                                  // Default provider: use selected provider if one is selected, otherwise empty (user can select in form)
                                  const defaultProviderId = selectedProviders?.length === 1 ? selectedProviders[0] : '';
                                  setAppointmentFormData({
                                    client_id: '',
                                    provider_id: defaultProviderId,
                                    start_time: datetimeLocal,
                                    end_time: endDatetimeLocal,
                                    service_type: '',
                                    appointment_type: 'client_session',
                                    is_billable: true,
                                    is_payroll_eligible: false,
                                    notes: '',
                                    location: '',
                                    units: 1
                                  });
                                  setEditingAppointment(null);
                                  setShowAppointmentForm(true);
                                } else {
                                  void 0 && console.log('❌ Slot not empty or has ongoing appointment');
                                }
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                handleDragOver(e, slotKey);
                              }}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => {
                                // Jun 2026 — deduped: the shared view-agnostic
                                // handleDrop (useCalendarDrag) replaces the old
                                // ~75-line inline copy so week view behaves
                                // identically to month/day (conflict checks,
                                // undo, optimistic update, 409 rollback).
                                if (draggedAppointment && !ongoingAppointment) {
                                  handleDrop(e, dayDate).then(() => { refreshStats?.(); });
                                } else {
                                  e.preventDefault();
                                  setDragOverSlot(null);
                                }
                              }}
                            >
                              {(() => {
                                // Calculate overlapping appointments for Google Calendar-style side-by-side layout
                                // Get all appointments for this day that could overlap
                                const dayAppointments = filteredAppointments.filter(apt => {
                                  const aptStart = new Date(apt.start_time);
                                  return aptStart.getDate() === dayDate.getDate() &&
                                         aptStart.getMonth() === dayDate.getMonth() &&
                                         aptStart.getFullYear() === dayDate.getFullYear();
                                });
                            
                                return slotAppointments.map(apt => {
                                // O(1) client lookup via clientByIdMap (Feb 2026
                                // perf fix — was Array#find on 5000 clients per appt
                                // per cell, every render).
                                const lookupClient = clientByIdMap.get(apt.client_id);
                                const clientDisplayName = getApptClientLabel(lookupClient, apt);
                                const aptStart = new Date(apt.start_time);
                                // Use preview end time if this appointment is being resized
                                const isBeingResized = resizingAppointment?.id === apt.id;
                                const aptEnd = isBeingResized ? new Date(resizingAppointment.previewEndTime) : new Date(apt.end_time);
                                const durationMinutes = (aptEnd - aptStart) / (1000 * 60);
                                // Exact proportional height (Jun 2026 regression
                                // fix): ceil-to-slot made a 10:00-10:20 block
                                // visually run to 10:30 and collide with a
                                // back-to-back 10:20 appt.
                                const heightPx = Math.max(10, (durationMinutes / calendarInterval) * 20);
                            
                                // Get overlap positioning (Google Calendar style)
                                const { totalOverlapping, positionIndex } = getOverlapInfo(apt, dayAppointments);
                                const widthPercent = totalOverlapping > 1 ? (100 / totalOverlapping) - 1 : 100; // -1 for gap
                                const leftPercent = positionIndex * (100 / totalOverlapping);
                            
                                // Find CPT code for this appointment to get color
                                const cptCode = resolveAppointmentCpt(apt, cptCodes);
                                const appointmentColor = cptCode?.color || getServiceTypeCategoryColor(apt, cptCodes, cptByIdMap, internalTypesById);
                            
                                // Get billing status border color
                                const billingBorderColor = getBillingStatusBorderColor(apt, notes, notesByApptMap);
                            
                                // Check if telehealth
                                const isTelehealth = isTelehealthAppointment(apt);
                                // Audio-only sub-type: phone icon + amber tint (Feb 2026
                                // user request: "if we have a audio only appointment, it
                                // would be good to have a visual cue of some sort… i do
                                // want to see the phone icon as well").
                                const isAudioOnly = apt.telehealth_modality === 'audio_only';
                            
                                // Calculate lighter versions of color for background and border
                                const rgb = hexToRgb(appointmentColor);
                            
                                // Different styling for No Show and Canceled
                                const isNoShow = apt.status === 'no_show';
                                const isCanceled = apt.status === 'cancelled' || apt.status === 'client_cancelled';
                            
                                const bgColor = isNoShow 
                                  ? 'rgba(156, 163, 175, 0.3)' // gray
                                  : isCanceled
                                  ? 'rgba(156, 163, 175, 0.2)' // lighter gray
                                  : isAudioOnly
                                  // Audio-only gets a distinctive warm amber tint so it
                                  // pops out from the video-telehealth + in-person sea
                                  // of the calendar at a glance.
                                  ? 'rgba(245, 158, 11, 0.18)' // amber-500 @ 18%
                                  : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`;
                                const borderColor = isNoShow || isCanceled
                                  ? 'rgba(156, 163, 175, 0.5)'
                                  : isAudioOnly
                                  ? 'rgba(217, 119, 6, 0.7)' // amber-600 @ 70%
                                  : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5)`;
                                // Use darker text for block_time (Personal Time) since bg is light mint
                                const isBlockTime = apt.appointment_type === 'block_time';
                                const textColor = isNoShow || isCanceled
                                  ? '#9CA3AF'
                                  : isTelehealth ? '#4F46E5' 
                                  : isBlockTime ? '#2D7A65' // Darker teal for Personal Time readability
                                  : getReadableChipTextColor(appointmentColor);
                                const opacity = isNoShow ? 0.6 : isCanceled ? 0.5 : 1;
                                // Feb 2026 — shrink cancelled / no_show
                                // appointments to HALF the slot width
                                // (anchored to the left edge) so a new
                                // appointment can be booked alongside
                                // in the same time slot.  User: "has to
                                // take up half the width so the provider
                                // can schedule something else."
                                // Height stays full so the chip remains
                                // readable and the original duration
                                // is still legible at a glance.
                                const inactiveShrink = (isNoShow || isCanceled);
                                const renderedWidthPercent = inactiveShrink
                                  ? Math.min(widthPercent, 50)
                                  : widthPercent;
                            
                                // Calculate offset within the 15-minute slot
                                const aptMinutes = aptStart.getMinutes();
                                const slotStartMinute = Math.floor(aptMinutes / calendarInterval) * calendarInterval;
                                const minuteOffset = aptMinutes - slotStartMinute;
                                const topOffsetPx = (minuteOffset / calendarInterval) * 20; // 20px per 15-min slot
                            
                                return (
                                  <div 
                                    key={apt.id} 
                                    className="absolute text-xs p-1 rounded shadow-sm hover:shadow-md transition-shadow"
                                    style={{
                                      cursor: 'grab',
                                      height: `${heightPx}px`,
                                      top: `${topOffsetPx}px`,
                                      left: `${leftPercent}%`,
                                      width: `${renderedWidthPercent}%`,
                                      backgroundColor: bgColor,
                                      borderLeft: billingBorderColor ? `3px solid ${isNoShow || isCanceled ? '#9CA3AF' : billingBorderColor}` : `3px solid ${borderColor}`,
                                      borderRight: `1px solid ${borderColor}`,
                                      borderTop: `1px solid ${borderColor}`,
                                      borderBottom: `1px solid ${borderColor}`,
                                      zIndex: inactiveShrink ? 2 : (5 + positionIndex), // Inactive sits below active so new bookings overlay it
                                      opacity: opacity,
                                      fontStyle: isNoShow || isCanceled ? 'italic' : 'normal',
                                      filter: isNoShow || isCanceled ? 'grayscale(0.8)' : 'none',
                                      pointerEvents: 'none',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <AppointmentHoverCard
                                      appointment={apt}
                                      clientDisplayName={clientDisplayName}
                                      providerName={providers?.find(p => p.id === apt.provider_id)?.full_name}
                                      onStatusChange={(updated) => {
                                        setAppointments(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a));
                                      }}
                                    >
                                    <DraggableAppt apt={apt} label={clientDisplayName}>
                                    <div 
                                      style={{pointerEvents: 'auto'}}
                                      onClick={(e) => {
                                      // Feb 2026 bug fix — when a chip
                                      // is rendered for a cancelled /
                                      // no_show appointment, the slot's
                                      // own ``onClick`` treats the slot
                                      // as empty (cancelled excluded
                                      // from `activeSlotAppointments`)
                                      // and would otherwise overwrite
                                      // the editing state we set below
                                      // with a NEW-appointment formData
                                      // bag, making it look like clicks
                                      // on cancelled appointments do
                                      // nothing.  Stop propagation so
                                      // the click only opens the edit
                                      // dialog.
                                      e.stopPropagation();
                                      // Only open form if not clicking resize handle AND not dragging
                                      if (!e.target.classList.contains('resize-handle') && !e.currentTarget.dataset.isDragging) {
                                        // Find CPT metadata for this appointment
                                        const aptCptCode = resolveAppointmentCpt(apt, cptCodes);
                                    
                                        // Format datetime for datetime-local input (local time)
                                        const formatLocalDateTime = (isoString) => {
                                          const date = new Date(isoString);
                                          const year = date.getFullYear();
                                          const month = String(date.getMonth() + 1).padStart(2, '0');
                                          const day = String(date.getDate()).padStart(2, '0');
                                          const hours = String(date.getHours()).padStart(2, '0');
                                          const minutes = String(date.getMinutes()).padStart(2, '0');
                                          return `${year}-${month}-${day}T${hours}:${minutes}`;
                                        };
                                    
                                        setAppointmentFormData({
                                          client_id: apt.client_id || '',
                                          client_ids: apt.client_ids || [],
                                          selected_group_id: apt.selected_group_id || '',
                                          provider_id: apt.provider_id,
                                          start_time: formatLocalDateTime(apt.start_time),
                                          end_time: formatLocalDateTime(apt.end_time),
                                          service_type: apt.service_type,
                                          appointment_type: apt.appointment_type || 'client_session',
                                          status: apt.status || 'scheduled',
                                          is_billable: apt.is_billable !== undefined ? apt.is_billable : true,
                                          is_payroll_eligible: apt.is_payroll_eligible || false,
                                          units: apt.units || 1,
                                          location: apt.location || '',
                                          pos_code: apt.pos_code || '',
                                          telehealth_modality: apt.telehealth_modality || '',
                                          cpt_code_id: apt.cpt_code_id || '',
                                          notes: apt.notes || '',
                                          selectedCptMeta: aptCptCode ? {
                                            allows_multiple_units: aptCptCode.allows_multiple_units,
                                            minutes_per_unit: aptCptCode.minutes_per_unit || aptCptCode.duration_minutes || 60,
                                            code: aptCptCode.code || aptCptCode.display_name,
                                            is_group_session: aptCptCode.is_group_session || false
                                          } : null
                                        });
                                        openAppointment(apt);
                                      }
                                    }}
                                    draggable
                                    onDragStart={(e) => {
                                      e.currentTarget.dataset.isDragging = 'true';
                                      setDraggedAppointment(apt);
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.currentTarget.style.cursor = 'grabbing';
                                  
                                      // Create a custom drag image
                                      const dragImage = e.currentTarget.cloneNode(true);
                                      dragImage.style.opacity = '0.7';
                                      dragImage.style.position = 'absolute';
                                      dragImage.style.top = '-1000px';
                                      document.body.appendChild(dragImage);
                                      e.dataTransfer.setDragImage(dragImage, 0, 0);
                                      setTimeout(() => document.body.removeChild(dragImage), 0);
                                    }}
                                    onDragEnd={(e) => {
                                      setDraggedAppointment(null);
                                      // Clear the dragging flag after a short delay to prevent click from firing
                                      setTimeout(() => {
                                        if (e.currentTarget) {
                                          e.currentTarget.dataset.isDragging = 'false';
                                        }
                                      }, 100);
                                    }}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className={`font-medium truncate ${isNoShow || isCanceled ? 'line-through' : ''}`} style={{color: textColor}}>
                                        {apt.client_id 
                                          ? clientDisplayName
                                          : (apt.appointment_type === 'group' 
                                             ? (clientGroups.find(g => g.id === (apt.selected_group_id || apt.group_id))?.name || 'Group Session')
                                             : apt.appointment_type === 'block_time' ? (apt.title || 'Personal Time') :
                                             apt.appointment_type === 'supervision' ? (apt.title || internalTypeName(apt) || 'Supervision') :
                                             apt.appointment_type === 'internal' ? (apt.title || internalTypeName(apt) || 'Internal Meeting') :
                                             apt.appointment_type === 'vacation' ? 'Vacation Day' :
                                             apt.appointment_type === 'sick_day' ? 'Sick Day' :
                                             (apt.title || apt.appointment_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Appointment'))
                                        }
                                      </div>
                                      {/* Invitation indicator for invited users */}
                                      {apt.invited_user_ids?.includes(user?.id) && apt.provider_id !== user?.id && (
                                        <span className={`text-[10px] ml-1 px-1 rounded ${
                                          apt.invitation_responses?.[user?.id] === 'accepted' ? 'bg-green-100 text-green-700' :
                                          apt.invitation_responses?.[user?.id] === 'declined' ? 'bg-red-100 text-red-700' :
                                          'bg-blue-100 text-blue-700'
                                        }`} title={apt.invitation_responses?.[user?.id] || 'Pending response'}>
                                          {apt.invitation_responses?.[user?.id] === 'accepted' ? '✓' :
                                           apt.invitation_responses?.[user?.id] === 'declined' ? '✗' : '📩'}
                                        </span>
                                      )}
                                      {(() => {
                                        const ind = getAppointmentEligibilityIndicator(apt, lookupClient);
                                        if (!ind) return null;
                                        return <span className={`text-[10px] ml-1 font-bold ${ind.color}`} title={ind.title}>{ind.label}</span>;
                                      })()}
                                      {renderCopayBadge(apt)}
                                    </div>
                                    <div className={`text-gray-600 text-[10px] truncate ${isNoShow || isCanceled ? 'line-through' : ''}`}>
                                      {(() => {
                                        // For group appointments, show CPT code (not group name repeated)
                                        if (apt.appointment_type === 'group') {
                                          const groupName = clientGroups.find(g => g.id === (apt.selected_group_id || apt.group_id))?.name;
                                          // If service_type is the same as group name, look up CPT code instead
                                          if (apt.service_type === groupName || !apt.service_type) {
                                            const groupCpt = apt.cpt_code_id ? cptCodes.find(c => c.id === apt.cpt_code_id) : null;
                                            return groupCpt ? `${groupCpt.code} - ${groupCpt.description || groupCpt.display_name || ''}`.trim() : (apt.service_type || '');
                                          }
                                        }
                                        return apt.service_type || '';
                                      })()}
                                    </div>
                                    <div className="flex items-center justify-between mt-0.5">
                                      <div className={`text-[9px] ${isBeingResized ? 'text-blue-600 font-bold' : 'text-gray-500'}`}>
                                        {durationMinutes}min
                                        {apt.units && apt.units > 1 && ` (${apt.units} units)`}
                                        {isBeingResized && ' ↕'}
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        {/* Meet button — only when:
                                            (a) a meet link exists,
                                            (b) POS is a telehealth code (02/10), AND
                                            (c) the appointment isn't block_time / personal.
                                            The (c) gate was missing and caused personal
                                            appointments switched from a meet → in-person
                                            to still surface the meet icon (Feb 2026 user
                                            report). */}
                                        {apt.google_meet_link
                                         && (apt.pos_code === '02' || apt.pos_code === '10')
                                         && apt.appointment_type !== 'block_time'
                                         && (
                                          <button 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openMeetPopup(apt.google_meet_link);
                                            }}
                                            className="text-blue-600 hover:text-blue-800"
                                            title={apt.appointment_type === 'group' 
                                              ? "Start session (popup) - Remember to use Waiting Room to admit group members" 
                                              : "Start session (opens in popup)"}
                                          >
                                            <Video className="h-3 w-3" />
                                          </button>
                                        )}
                                        {/* Telehealth indicator when no Meet link.
                                            Same gate — never on block_time or audio-only
                                            (audio-only has its own Phone icon below). */}
                                        {isTelehealth
                                         && !apt.google_meet_link
                                         && !isAudioOnly
                                         && apt.appointment_type !== 'block_time'
                                         && (
                                          <span className="text-indigo-500" title="Telehealth appointment">
                                            <Video className="h-3 w-3" />
                                          </span>
                                        )}
                                        {/* Audio-only visual cue.  Suppressed for
                                            block_time too — personal time isn't a
                                            clinical telehealth session. */}
                                        {isAudioOnly && apt.appointment_type !== 'block_time' && (
                                          <span className="text-amber-600" title="Audio-only telehealth (Phone)">
                                            <Phone className="h-3 w-3" />
                                          </span>
                                        )}
                                        {apt.status && apt.status !== 'scheduled' && (
                                          <span className="text-[8px] px-1 py-0.5 rounded bg-gray-200">{apt.status}</span>
                                        )}
                                      </div>
                                    </div>
                                
                                    {/* Resize handle at bottom - only show if appointment can be resized */}
                                    {canResizeAppointment(apt) && (
                                    <div 
                                      className="resize-handle absolute bottom-0 left-0 right-0 h-1 hover:h-2 hover:bg-blue-500 cursor-ns-resize transition-all"
                                      style={{backgroundColor: billingBorderColor}}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    
                                        const startY = e.clientY;
                                        const startHeight = heightPx;
                                        const startEndTime = new Date(apt.end_time);
                                    
                                        const handleMouseMove = (moveEvent) => {
                                          const deltaY = moveEvent.clientY - startY;
                                          const deltaSlots = Math.round(deltaY / 20); // 20px per slot
                                          const newSlots = Math.max(1, slots + deltaSlots);
                                          let previewDuration = newSlots * 15; // 15 minutes per slot
                                      
                                          // Get CPT code for validation
                                          const aptCptCode = resolveAppointmentCpt(apt, cptCodes);
                                      
                                          // Cap preview duration based on CPT constraints
                                          if (aptCptCode) {
                                            // If CPT doesn't allow multiple units, cap at single unit duration
                                            if (aptCptCode.allows_multiple_units === false) {
                                              const singleUnitDuration = aptCptCode.max_duration || aptCptCode.minutes_per_unit || aptCptCode.duration_minutes || 60;
                                              previewDuration = Math.min(previewDuration, singleUnitDuration);
                                            }
                                            // Also respect max_duration
                                            if (aptCptCode.max_duration && previewDuration > aptCptCode.max_duration) {
                                              previewDuration = aptCptCode.max_duration;
                                            }
                                            // And min_duration
                                            if (aptCptCode.min_duration && previewDuration < aptCptCode.min_duration) {
                                              previewDuration = aptCptCode.min_duration;
                                            }
                                          }
                                      
                                          const aptStart = new Date(apt.start_time);
                                          const newEndTime = new Date(aptStart.getTime() + previewDuration * 60000);
                                      
                                          // Live visual feedback - update preview state
                                          setResizingAppointment({ id: apt.id, previewEndTime: newEndTime.toISOString(), previewDuration: previewDuration });
                                        };
                                    
                                        const handleMouseUp = async (upEvent) => {
                                          const deltaY = upEvent.clientY - startY;
                                          const deltaSlots = Math.round(deltaY / 20);
                                          const newSlots = Math.max(1, slots + deltaSlots);
                                          const newDuration = newSlots * 15;
                                      
                                          const aptStart = new Date(apt.start_time);
                                          const newEndTime = new Date(aptStart.getTime() + newDuration * 60000);
                                      
                                          // Prevent client sessions from crossing midnight
                                          if (apt.appointment_type === 'client_session' && aptStart.toDateString() !== newEndTime.toDateString()) {
                                            toast.error("Client appointments cannot cross midnight");
                                            document.removeEventListener('mousemove', handleMouseMove);
                                            document.removeEventListener('mouseup', handleMouseUp);
                                            return;
                                          }
                                      
                                          // Calculate units based on CPT code's minutes_per_unit
                                          const aptCptCode = resolveAppointmentCpt(apt, cptCodes);
                                      
                                          // Smart snapping: If CPT has min/max, snap to nearest valid duration
                                          let finalDuration = newDuration;
                                          if (aptCptCode?.min_duration || aptCptCode?.max_duration) {
                                            if (aptCptCode.min_duration && newDuration < aptCptCode.min_duration) {
                                              finalDuration = aptCptCode.min_duration;
                                              toast.warning(`Minimum duration for ${aptCptCode.code} is ${finalDuration} minutes — snapped to the minimum.`);
                                            } else if (aptCptCode.max_duration && newDuration > aptCptCode.max_duration) {
                                              finalDuration = aptCptCode.max_duration;
                                              toast.warning(`Maximum duration for ${aptCptCode.code} is ${finalDuration} minutes — snapped to the maximum.`);
                                            }
                                          }
                                      
                                          // Recalculate end time with snapped duration
                                          const finalEndTime = new Date(aptStart.getTime() + finalDuration * 60000);
                                      
                                          const minutesPerUnit = aptCptCode?.minutes_per_unit || aptCptCode?.duration_minutes || 60;
                                          let newUnits = Math.max(1, Math.round(finalDuration / minutesPerUnit));
                                      
                                          // Enforce single unit if CPT doesn't allow multiple units
                                          if (aptCptCode && aptCptCode.allows_multiple_units === false && newUnits > 1) {
                                            newUnits = 1;
                                            // Also cap duration to the single unit duration
                                            finalDuration = minutesPerUnit;
                                            const cappedEndTime = new Date(aptStart.getTime() + finalDuration * 60000);
                                            toast.warning(`${aptCptCode.code} does not allow multiple units. Limited to 1 unit (${finalDuration} min).`);
                                            // Update finalEndTime reference for the API call
                                            var adjustedEndTime = cappedEndTime;
                                          } else {
                                            var adjustedEndTime = finalEndTime;
                                          }
                                      
                                          try {
                                            // Save previous state for undo
                                            addToUndoStack('update', {
                                              id: apt.id,
                                              start_time: apt.start_time,
                                              end_time: apt.end_time,
                                              client_id: apt.client_id,
                                              provider_id: apt.provider_id,
                                              service_type: apt.service_type,
                                              units: apt.units
                                            });
                                        
                                            // OPTIMISTIC UPDATE: Immediately update in local state
                                            const newEndTimeIso = adjustedEndTime.toISOString();
                                            setAppointments(prev => prev.map(a => 
                                              a.id === apt.id 
                                                ? { ...a, end_time: newEndTimeIso, units: newUnits }
                                                : a
                                            ));
                                        
                                            // API call in background
                                            await axios.patch(`${API}/appointments/${apt.id}`, {
                                              end_time: newEndTimeIso,
                                              units: newUnits
                                            });
                                            toast.success(`Appointment updated: ${finalDuration}min (${newUnits} units) - Ctrl+Z to undo`);
                                          } catch (error) {
                                            console.error('Error updating appointment:', error);
                                            toast.error('Failed to update appointment');
                                            // Rollback on error
                                            refreshAppointments(true);
                                          }
                                      
                                          document.removeEventListener('mousemove', handleMouseMove);
                                          document.removeEventListener('mouseup', handleMouseUp);
                                          setResizingAppointment(null); // Clear resize preview
                                        };
                                    
                                        document.addEventListener('mousemove', handleMouseMove);
                                        document.addEventListener('mouseup', handleMouseUp);
                                      }}
                                    />
                                    )}
                                  </div>
                                  </DraggableAppt>
                                  </AppointmentHoverCard>
                                  </div>
                                );
                              });
                              })()}
                            </DroppableCell>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : calendarView === 'list' ? (
          /* List view - simplified list with date headers */
          <div className="space-y-4">
            {/* Search and Controls */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Appointments</h3>
                <div className="flex items-center space-x-4">
                  {/* Include Past Toggle */}
                  <div className="flex items-center space-x-2">
                    <label htmlFor="include-past" className="text-sm text-gray-700">
                      Include Past
                    </label>
                    <button
                      id="include-past"
                      type="button"
                      role="switch"
                      aria-checked={showPastAppointments}
                      onClick={() => setShowPastAppointments(!showPastAppointments)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                        showPastAppointments ? 'bg-teal-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          showPastAppointments ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
              
                  <div className="w-px h-6 bg-gray-300"></div>
              
                  {/* Sort Toggle */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAppointmentSortAsc(!appointmentSortAsc)}
                    className="flex items-center space-x-2"
                  >
                    {appointmentSortAsc ? (
                      <>
                        <span>Soonest First</span>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                        </svg>
                      </>
                    ) : (
                      <>
                        <span>Furthest First</span>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                        </svg>
                      </>
                    )}
                  </Button>
                </div>
              </div>
          
              {/* Search Box — standard format (Jun 2026). */}
              <div className="relative">
                <SearchIcon className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search appointments by client name, CPT code, or date..."
                  value={appointmentSearch}
                  onChange={(e) => setAppointmentSearch(e.target.value)}
                  className="w-full h-9 pl-8 pr-4 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="calendar-list-search-input"
                />
                {appointmentSearch && (
                  <button
                    onClick={() => setAppointmentSearch('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {appointmentSearch.trim().length >= 3 && (searchExtraLoading || searchExtraAppointments.length > 0) && (
                <p className="text-xs text-gray-500" data-testid="appointment-search-fullhistory-hint">
                  {searchExtraLoading
                    ? 'Searching full appointment history…'
                    : 'Showing full appointment history for matching clients (turn on "Include Past" to see older visits)'}
                </p>
              )}
            </div>
        
            {(() => {
              // Merge full-history rows fetched by the client-name
              // search (outside the 6-week calendar window), applying
              // the same provider / cancelled filters as the base set.
              let mergedRows = filteredAppointments;
              if (appointmentSearch && appointmentSearch.trim() && searchExtraAppointments.length > 0) {
                const seenIds = new Set((appointments || []).map((a) => a.id));
                let extras = searchExtraAppointments.filter((a) => a?.id && !seenIds.has(a.id));
                if (!(selectedProviders.includes('all') || (_selected.length === 0 && !_isExclude))) {
                  extras = extras.filter((apt) => _isExclude
                    ? (!_selected.includes(apt.provider_id) || (apt.invited_user_ids || []).includes(user?.id))
                    : (_selected.includes(apt.provider_id) || (apt.invited_user_ids || []).includes(user?.id)));
                }
                if (!showCanceledAppointments) {
                  extras = extras.filter((apt) =>
                    apt.status !== 'canceled' &&
                    apt.status !== 'cancelled' &&
                    apt.status !== 'client_cancelled'
                  );
                }
                mergedRows = [...filteredAppointments, ...extras];
              }

              // Filter by time (past/future)
              const today = new Date();
              today.setHours(0, 0, 0, 0); // Start of today
          
              let filteredByTime = mergedRows.filter(appointment => {
                const appointmentDate = new Date(appointment.start_time);
                if (showPastAppointments) {
                  return true; // Show all appointments (past and future)
                } else {
                  return appointmentDate >= today; // Only show today and future
                }
              });
          
              // Filter by search term
              if (appointmentSearch && appointmentSearch.trim()) {
                const searchLower = appointmentSearch.toLowerCase();
                filteredByTime = filteredByTime.filter(appointment => {
                  // O(1) client lookup via clientByIdMap (Feb 2026 perf
                  // fix — was N appts × C clients per keystroke during
                  // search; keystroke stutters in the list view came
                  // straight from this).
                  const client = clientByIdMap.get(appointment.client_id);
                  const clientNameMatch = (
                    client?.full_name?.toLowerCase().includes(searchLower) ||
                    client?.preferred_name?.toLowerCase().includes(searchLower)
                  );
                  const cptMatch = appointment.service_type?.toLowerCase().includes(searchLower);
                  const dateMatch = new Date(appointment.start_time).toLocaleDateString().includes(appointmentSearch);
                  const dateFormatMatch = new Date(appointment.start_time).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  }).toLowerCase().includes(searchLower);
              
                  // Day of week search (e.g., "friday", "monday")
                  const dayOfWeek = new Date(appointment.start_time).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                  const dayMatch = dayOfWeek.includes(searchLower);
              
                  // Location search - check location name and "outreach" for POS 12
                  const locationName = appointment.location 
                    ? (locations.find(l => l.id === appointment.location)?.name || appointment.location)
                    : '';
                  const locationMatch = locationName?.toLowerCase().includes(searchLower);
                  const outreachMatch = appointment.pos_code === '12' && 'outreach'.includes(searchLower);
              
                  // Telehealth search
                  const isTelehealth = appointment.is_telehealth || appointment.pos_code === '02' || appointment.pos_code === '10';
                  const telehealthMatch = isTelehealth && ('telehealth'.includes(searchLower) || 'video'.includes(searchLower) || 'virtual'.includes(searchLower));
              
                  // Group name search
                  const groupName = appointment.appointment_type === 'group' 
                    ? (clientGroups.find(g => g.id === (appointment.selected_group_id || appointment.group_id))?.name || '')
                    : '';
                  const groupMatch = groupName?.toLowerCase().includes(searchLower);
              
                  return clientNameMatch || cptMatch || dateMatch || dateFormatMatch || dayMatch || locationMatch || outreachMatch || telehealthMatch || groupMatch;
                });
              }
          
              // Sort appointments by date
              const sortedAppointments = [...filteredByTime].sort((a, b) => {
                const dateA = new Date(a.start_time).getTime();
                const dateB = new Date(b.start_time).getTime();
                return appointmentSortAsc ? dateA - dateB : dateB - dateA;
              });
          
              // Group appointments by date
              const groupedByDate = sortedAppointments.reduce((acc, appointment) => {
                const date = new Date(appointment.start_time);
                const dateKey = date.toDateString(); // e.g., "Mon Jan 15 2024"
            
                if (!acc[dateKey]) {
                  acc[dateKey] = [];
                }
                acc[dateKey].push(appointment);
                return acc;
              }, {});
          
              // Get sorted date keys
              const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
                const dateA = new Date(a).getTime();
                const dateB = new Date(b).getTime();
                return appointmentSortAsc ? dateA - dateB : dateB - dateA;
              });
          
              return sortedDates.map(dateKey => {
                const appointmentsForDate = groupedByDate[dateKey];
                const date = new Date(dateKey);
                const today = new Date();
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
            
                // Format date header
                const isToday = date.toDateString() === today.toDateString();
                const isTomorrow = date.toDateString() === tomorrow.toDateString();
            
                let dateLabel = '';
                if (isToday) {
                  dateLabel = 'Today';
                } else if (isTomorrow) {
                  dateLabel = 'Tomorrow';
                } else {
                  // Format as "Monday, January 15, 2024"
                  dateLabel = date.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                }
            
                return (
                  <div key={dateKey} className="space-y-3">
                    {/* Date Header */}
                    <div className="sticky top-0 bg-white z-10 py-2 border-b-2 border-teal-500">
                      <div className="flex items-center space-x-3">
                        <div className="bg-teal-100 px-4 py-2 rounded-lg">
                          <h4 className="font-semibold text-teal-900">
                            {dateLabel}
                          </h4>
                          <p className="text-xs text-teal-700">
                            {appointmentsForDate.length} appointment{appointmentsForDate.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="text-sm text-gray-500">
                          {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                    </div>
                
                    {/* Appointments for this date */}
                    {appointmentsForDate.map((appointment) => {
                      // O(1) client lookup via clientByIdMap (Feb 2026 perf fix).
                      const lookupClient = clientByIdMap.get(appointment.client_id);
                      const clientDisplayName = getApptClientLabel(lookupClient, appointment);
                      const startTime = new Date(appointment.start_time);
                  
                      // Get visual styling
                      const serviceColor = getServiceTypeCategoryColor(appointment, cptCodes, cptByIdMap, internalTypesById);
                      const billingBorder = getBillingStatusBorderColor(appointment, notes, notesByApptMap);
                      const isTelehealth = isTelehealthAppointment(appointment);
                      const isNoShow = appointment.status === 'no_show';
                      const isCanceled = appointment.status === 'cancelled' || appointment.status === 'client_cancelled';
                      const isBlockTime = appointment.appointment_type === 'block_time';
                  
                      const textColorVal = isNoShow || isCanceled 
                        ? '#6B7280' // gray-500 for cancelled/no-show
                        : (isTelehealth ? '#3730A3' : (isBlockTime ? '#1E5A4B' : getContrastTextColor(serviceColor)));
                  
                      return (
                        <Card 
                          key={appointment.id} 
                          className={`p-4 ml-4 ${isNoShow || isCanceled ? 'opacity-60' : ''} cursor-pointer`}
                          style={{
                            backgroundColor: isNoShow || isCanceled ? 'rgba(156, 163, 175, 0.1)' : hexToRgba(serviceColor, 0.08),
                            borderLeft: `4px solid ${isNoShow || isCanceled ? '#9CA3AF' : billingBorder}`
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setAppointmentContextMenu({
                              show: true,
                              x: e.clientX,
                              y: e.clientY,
                              appointment: appointment
                            });
                          }}
                          title="Right-click for options"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 
                                className={`font-semibold text-lg ${isNoShow || isCanceled ? 'line-through text-gray-500' : ''}`}
                                style={{ color: textColorVal }}
                              >
                                {appointment.client_id 
                                  ? clientDisplayName
                                  : (appointment.appointment_type === 'group' 
                                     ? (clientGroups.find(g => g.id === (appointment.selected_group_id || appointment.group_id))?.name || 'Group Session')
                                     : appointment.appointment_type === 'block_time' ? 'Personal Time' :
                                     appointment.appointment_type === 'supervision' ? (internalTypeName(appointment) || 'Supervision') :
                                     appointment.appointment_type === 'internal' ? (internalTypeName(appointment) || 'Internal Meeting') :
                                     appointment.appointment_type === 'vacation' ? 'Vacation Day' :
                                     appointment.appointment_type === 'sick_day' ? 'Sick Day' :
                                     appointment.appointment_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()))
                                }
                              </h3>
                              <p className={`text-gray-600 ${isNoShow || isCanceled ? 'line-through' : ''}`}>
                                {(() => {
                                  // For group appointments, show CPT code (not group name repeated)
                                  if (appointment.appointment_type === 'group') {
                                    const groupName = clientGroups.find(g => g.id === (appointment.selected_group_id || appointment.group_id))?.name;
                                    // If service_type is the same as group name, look up CPT code instead
                                    if (appointment.service_type === groupName || !appointment.service_type) {
                                      const groupCpt = appointment.cpt_code_id ? cptCodes.find(c => c.id === appointment.cpt_code_id) : null;
                                      return groupCpt ? `${groupCpt.code} - ${groupCpt.description || groupCpt.display_name || ''}`.trim() : (appointment.service_type || '');
                                    }
                                  }
                                  return appointment.service_type || '';
                                })()}
                              </p>
                              <div className={`flex items-center space-x-4 text-sm mt-1 ${isTelehealth ? 'text-indigo-600' : 'text-gray-600'}`}>
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                {/* Telehealth indicator - show camera icon and Join Meeting link */}
                                {isTelehealth ? (
                                  <div className="flex items-center">
                                    <Video className="h-4 w-4 mr-1 text-indigo-500" />
                                    {appointment.google_meet_link ? (
                                      <>
                                      <a 
                                        href={appointment.google_meet_link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[#10B981] hover:underline font-semibold"
                                        data-testid={`meet-link-${appointment.id}`}
                                      >
                                        Join Meeting
                                      </a>
                                      <button
                                        type="button"
                                        className="ml-2 p-1 rounded text-gray-400 hover:text-[#10B981] hover:bg-emerald-50"
                                        title="Copy video link to send to the client"
                                        data-testid={`copy-meet-link-${appointment.id}`}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await navigator.clipboard.writeText(appointment.google_meet_link);
                                            toast.success('Video link copied — paste it into an email or text to the client');
                                          } catch {
                                            window.prompt('Copy the video link below:', appointment.google_meet_link);
                                          }
                                        }}
                                      >
                                        <Copy className="h-3.5 w-3.5" />
                                      </button>
                                      </>
                                    ) : (
                                      <span className="text-gray-400 text-xs">Telehealth (no link)</span>
                                    )}
                                  </div>
                                ) : (
                                  /* In-person - show location with appropriate icon */
                                  (() => {
                                    const locationName = appointment.location 
                                      ? (locations.find(l => l.id === appointment.location)?.name || appointment.location)
                                      : null;
                                    const isClientHome = appointment.pos_code === '12';
                                
                                    // For POS 12 (Home), show "Outreach" instead of billing office
                                    const displayLocation = isClientHome ? 'Outreach' : locationName;
                                
                                    return displayLocation && (
                                      <div className="flex items-center text-gray-600">
                                        {isClientHome ? (
                                          <Home className="h-4 w-4 mr-1" />
                                        ) : (
                                          <Building className="h-4 w-4 mr-1" />
                                        )}
                                        <span>{displayLocation}</span>
                                      </div>
                                    );
                                  })()
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {/* Facilitator Tip for Group Telehealth - show as tooltip on hover */}
                              {appointment.google_meet_link && appointment.appointment_type === 'group' && (
                                <div 
                                  className="relative group"
                                  title="Facilitator Tip: Join a few minutes early to be the host. Use the Waiting Room feature to admit group members individually."
                                >
                                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-help flex items-center gap-1">
                                    <Info className="h-3 w-3" />
                                    Group Host
                                  </Badge>
                                </div>
                              )}
                              <Badge className={getStatusBadgeColor(appointment.status)}>
                                {appointment.status}
                              </Badge>
                              {/* Vitals Check Badge - only show to provider who set the reminder */}
                              {appointment.has_vitals_check && appointment.vitals_check_provider_id === user?.id && (
                                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 flex items-center gap-1">
                                  <Activity className="h-3 w-3" />
                                  Vitals Check
                                </Badge>
                              )}
                              {/* View Note Button - if note exists for this appointment */}
                              {(() => {
                                const linkedNote = notes.find(n => n.appointment_id === appointment.id);
                                const hasLinkedNote = linkedNote || appointment.created_from_note;
                                if (hasLinkedNote) {
                                  return (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={async () => {
                                        if (linkedNote) {
                                          openNote(linkedNote);
                                        } else if (appointment.created_from_note) {
                                          // Note not in local state yet — fetch and open
                                          openNote({id: appointment.created_from_note});
                                        }
                                      }}
                                      title="View note"
                                      className="hover:bg-blue-100 text-blue-600"
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                  );
                                }
                              })()}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openAppointment(appointment)}
                                title="Edit appointment"
                                className="hover:bg-gray-100"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                );
              });
            })()}
        
            {filteredAppointments.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No appointments scheduled. Schedule your first appointment to get started.
              </div>
            )}
        
            {filteredAppointments.length > 0 && (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              let filtered = filteredAppointments.filter(appointment => {
                const appointmentDate = new Date(appointment.start_time);
                return showPastAppointments ? true : appointmentDate >= today;
              });
          
              // Apply search filter
              if (appointmentSearch && appointmentSearch.trim()) {
                const searchLower = appointmentSearch.toLowerCase();
                filtered = filtered.filter(appointment => {
                  const client = clientByIdMap.get(appointment.client_id);
                  const clientNameMatch = (
                    client?.full_name?.toLowerCase().includes(searchLower) ||
                    client?.preferred_name?.toLowerCase().includes(searchLower)
                  );
                  const cptMatch = appointment.service_type?.toLowerCase().includes(searchLower);
                  const dateMatch = new Date(appointment.start_time).toLocaleDateString().includes(appointmentSearch);
                  const dateFormatMatch = new Date(appointment.start_time).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  }).toLowerCase().includes(searchLower);
              
                  // Day of week search (e.g., "friday", "monday")
                  const dayOfWeek = new Date(appointment.start_time).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                  const dayMatch = dayOfWeek.includes(searchLower);
              
                  // Location search - check location name and "outreach" for POS 12
                  const locationName = appointment.location 
                    ? (locations.find(l => l.id === appointment.location)?.name || appointment.location)
                    : '';
                  const locationMatch = locationName?.toLowerCase().includes(searchLower);
                  const outreachMatch = appointment.pos_code === '12' && 'outreach'.includes(searchLower);
              
                  // Telehealth search
                  const isTelehealth = appointment.is_telehealth || appointment.pos_code === '02' || appointment.pos_code === '10';
                  const telehealthMatch = isTelehealth && ('telehealth'.includes(searchLower) || 'video'.includes(searchLower) || 'virtual'.includes(searchLower));
              
                  // Group name search
                  const groupName = appointment.appointment_type === 'group' 
                    ? (clientGroups.find(g => g.id === (appointment.selected_group_id || appointment.group_id))?.name || '')
                    : '';
                  const groupMatch = groupName?.toLowerCase().includes(searchLower);
              
                  return clientNameMatch || cptMatch || dateMatch || dateFormatMatch || dayMatch || locationMatch || outreachMatch || telehealthMatch || groupMatch;
                });
              }
          
              if (filtered.length === 0 && !searchExtraLoading && searchExtraAppointments.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    {appointmentSearch 
                      ? `No appointments match "${appointmentSearch}"` 
                      : !showPastAppointments 
                      ? 'No upcoming appointments. Your schedule is clear!' 
                      : 'No appointments found.'}
                  </div>
                );
              }
              return null;
            })()}
            {typeof onLoadMoreList === 'function' && !appointmentSearch && (
              <div className="flex flex-col items-center gap-1 pt-2 pb-1" data-testid="list-load-more-wrap">
                {listRangeLabel && (
                  <span className="text-xs text-gray-400">{listRangeLabel}</span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onLoadMoreList}
                  data-testid="list-load-more-btn"
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        ) : null;
        })()}
      </CardContent>
    </Card>
    </div>
    </CalendarDndProvider>
  );
};

export default AppointmentsTab;
